/**
 * @module Support
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
/* tslint:disable:max-file-line-count */
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { Reason } from '@support/api/proxies/responses/api-326.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { FormGroup } from '@angular/forms';
// api endpoints
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
// interfaces
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { TppSession } from '@support/shared/interfaces/tpp-session.interface';
import {
  Cart,
  CartItem,
  CartLine,
  CartResponse,
  Line,
  RemorseExchangeRequestPayload,
  ScreenDetails,
  Usku,
  AddressList,
} from '@support/api/proxies/responses/api-cart-response.interface';
import {
  CartAuthenticationAEMData,
  ConfigurableCartAEMData,
} from '@core/aem/interfaces/shop/cart.interface';
import { Api2119Request } from '@support/api/proxies/requests/api-2119.interface';
import { UpdateAccessory } from '@support/api/proxies/requests/api-2105.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { StoreData } from '@experience/store-operations/interfaces/store-ops.interface';
import { AddPromoCodeRequest } from '@support/api/proxies/requests/api-add-promo.interface';
import { Product, ProductRequest, Sku, SkuPrice } from '@support/api/common/product.interface';
import {
  Category,
  EligibleServices,
  ServiceFamily,
} from '@support/api/proxies/responses/api-2102.interface';
import { CartService } from '@support/api/common/retrieveServices.interface';
import { APIError } from '@support/api/common/error.interface';
import { ValidateSimResponse } from '@support/api/proxies/responses/api-2900.interface';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { DeviceData } from '@support/api/proxies/requests/api-2170.interface';
import { ImeiCheckInterface } from '@support/api/proxies/responses/api-2170.interface';
import { UpdatePlansRequest } from '@support/api/proxies/requests/api-2109.interface';
import {
  CheckoutCart,
  HazmatPayloadInterface,
} from '@experience/one-time-payment/interfaces/cart-request-interface';
// constants
import { SYMBOL_CONSTANTS } from '@support/shared/constants/device.constants';
import {
  BillingTypeEnum,
  CreditLevelEnum,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { PRODUCT_SELECTOR_CONSTANTS } from '@experience/shop/product-selector/constants/product-selector.constants';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import {
  OFFER_CONSTANTS,
  ORDER_MODULE_CONSTANT,
  ORDER_TYPE,
  PRICING_TYPE,
  FLAG_STATUS
} from '@experience/shop/shop.constants';
import { CART_MODULE_COMPONENTS } from '@experience/shop/cart/constants/cart.constants';
import {
  AUDIT_CONTEXT,
  CART_ITEM_STATUS,
  CART_SUPPORT_MODULE,
  CART_SUPPORT_SERVICE,
  ORDER_ACTION_TYPE,
  ORDER_SUBMIT_SUCCESS,
  PAYMENT_ROUTES,
  TRANSACTION_TYPE,
  ORDER_SUBMIT_ERROR_CODE,
  PAYMENT_ERROR_CODE,
  CONFIRMATION_ROUTE,
  PUBLISH_ERROR_CODE,
  CHECKOUT_ERROR_CODE,
  CART_ADDRESS_USAGE,
} from '@support/cart-support/constants/cart-support-service-constants';
import { CART_LANDING_CONSTANTS } from '@experience/shop/cart/constants/cart-landing.constants';
import { CART_ROUTES } from '@support/shared/constants/app-routes.constants';
// Model
import { Tile } from '@experience/shop/product-selector/models/tile.model';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { CartUtils } from '@support/cart-support/cart-utils';
import { ShoppingCart } from '@experience/shop/cart/models/cart.model';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import {
  Observable,
  Subject,
  BehaviorSubject,
  Subscriber,
  throwError as observableThrowError,
  of as observableOf,
  ReplaySubject,
} from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { Wove } from 'aspect.js';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { StoreService } from '@support/store/services/store.service';
import { OrderPaymentModel } from '@experience/shop/order-payment/online-order-payment/models/online-order-payment.model';
import {
  CartLineItem,
  CartNonLineItem,
  CartValidatedItem,
  ReplacementItem,
} from '@experience/shop/cart/interfaces/cart-review.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';
import {
  EpinConflictResponse,
  ProductTypes,
} from '@experience/shop/product-details/interfaces/product-details.interface';
import {
  ErrorMessage,
  OrderLine,
  SubmitOrderResponse,
} from '@support/api/proxies/responses/api-2150.interface';
import { OnlineSubmitOrder } from '@support/payment/online-payment/models/online-submit-order.model';
import { SHARED_PAYMENT } from '@support/payment/shared-payment/constants/shared-payment.constants';
import { CART_REVIEW_CONST } from '@experience/shop/cart/constants/cart-review.constants';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { CustomerDetailsResponse } from '@support/api/proxies/responses/api-2007.interface';
import { ShippingOverridePriceResponse } from '@support/shared/widgets/interfaces/override-price-modal.interface';
import { UIPermissionService } from '@support/shared/widgets/services/ui-permission.service';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { AppConfig } from '@root/app.config';
import { FAN } from '@support/api/proxies/responses/api-2038.interface';
import { AddSkuItemsToNonLineInterface } from '@support/api/proxies/requests/api-2107.interface';
import { ValidateImeiAndSim } from '@support/api/proxies/responses/api_2168.interface';
import { ValidateImeiAndSimRequest } from '@support/api/proxies/requests/api_2168.interface';
import { map, catchError, last, concat, takeUntil } from 'rxjs/operators';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import {
  OrderSummaryInterface,
  PaymentMethods,
  PriceCollection,
} from '@support/api/proxies/responses/api-2814-new.interface';
import { NumberManagementOperations } from '@experience/change-msisdn/new-number/interface/new-number.interface';
import { UserService } from '@support/shared/services/user.service';
import { CancelOrderDetails } from '@support/api/proxies/responses/api-2815.interface';
import { ACTION_CODE_CONSTANTS } from '@experience/modify-cancellation/constants/modify-select-lines.constants';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { randomNumber } from '@support/shared/services/random-number.helper';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import {
  PDL_CART_PATH,
  PDL_DIGITAL_PATH,
  PDL_ORDER,
  PDL_SHIPPING_PATH,
  SYMBOL_CONSTANTS as PDL_SYMBOL,
} from '@experience/reb-pdl/constants/pdl.constants';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';

@Wove({ moduleName: CART_SUPPORT_MODULE })
@Injectable({
  providedIn: 'root',
})
export class CartSupportService extends PDLBaseComponent {
  /**
   * Subject for the shipping price
   */
  public selectedShippingPrice: Subject<String>;
  /**
   * Holds the value of selected shipping option price
   */
  public selectedPrice$: Observable<String>;
  /**
   * stores family name of the selected device
   */
  public productName: string;
  /**
   * stores selected country name
   */
  public selectedCountry: string;
  /**
   * stores number of devices
   */
  public moreThanOneDevice: boolean;
  /**
   * checks if a product is selected
   */
  public productExists: boolean;
  /**
   * Checks if tourist simkit is selected
   */
  public isTouristSimkit: boolean;
  /**
   * Subscription source - allows multiple observers to receive broadcasted changes
   */
  public cartUpdated$: Observable<boolean>;
  public goToCartSelectionUpdated$: Observable<boolean>;
  public planSelectedSource: Subject<string>;
  public cartCountUpdated$: Observable<void>;
  public planSelected$: Observable<string>;
  public auditData: Subject<object>;
  public auditData$: Observable<object>;
  public productSelectorSpinner$: Observable<boolean>;
  /**
   * Holds the value of shipping amount waived as a Subject
   */
  public shippingWaiverSubject: Subject<string>;
  /**
   * Holds the value of shipping amount waived Data as an Observable
   */
  public shippingWaiverSubject$: Observable<string>;
  /**
   * Holds the value of transaction id as a Subject
   */
  public overrideTransactionIdSubject: Subject<string>;
  /**
   * Holds the value of transaction id Data as an Observable
   */
  public overrideTransactionIdSubject$: Observable<string>;
  /**
   * display taxes is used to show tax on pricing panel
   */
  public displayTaxes: EventEmitter<boolean>;

  /**
   * Flag to enable/disable the focus of sku
   */
  public deviceAndSimToCart$: Observable<boolean>;
  /**
   * this is the subject created for detecting fraud occurance
   */
  public fraudDetectionSource: Subject<boolean>;
  /**
   * Subscription fraudDetectionUpdated- allows multiple observers to receive broadcasted changes
   */
  public fraudDetectionUpdated$: Observable<boolean>;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   * Making it public to fix TSLint issues in TestCase
   */
  public cartUpdatedSource: Subject<boolean>;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public cartCountUpdatedSource: Subject<void>;

  /**
   * go to cart selection update
   */
  public selectionDeviceUpdated: Subject<boolean>;
  /**
   * used to show error Messages on API 2150 failure
   */
  public submitOrderErrorMessage: BehaviorSubject<ErrorMessage[]>;
  /**
   * Stream of cart title updates.
   */
  public cartTitleUpdate: Subject<string>;
  /**
   * string value to identify current page
   */
  public currentPage: Subject<string>;

  public shippingPrice: string;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  private productSelectorSpinnerSource: Subject<boolean>;
  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  private deviceAndSimToCartSource: Subject<boolean>;
  /**
   * Stores the timeout for expiring reserved numbers
   * @type {number}
   */
  public fraudApiTimeout: number;
  /**
   * Stores the value of window variable used for setting timeout
   * @type Window
   */
  public readonly applicationWin: Window;
  /**
   * Unsubscriber subject for all unsubscribes
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * Emitter to disable/enable checkout button
   */
  public maxEpinsReached: EventEmitter<boolean>;
  /**
   * Emitter to emit true on cart-authentication page
   */
  public cartAuthEmitter: EventEmitter<boolean>;
  /**
   * emitter to emit updated cart on device removal
   */
  public updateCart: EventEmitter<boolean>;
  /**
   * flag to enable of disable email text field
   */
  public enableEmail: boolean;
  /**
   * checks if any device has been removed from cart
   */
  public updateDeviceValue: boolean;
  public currentMessage: Subject<boolean>;
  public currentMessage$: Observable<boolean>;
  /**
   * Subject to notify that cart is updated
   */
  public completeCartUpdatedSource: Subject<boolean>;
  /**
   * Oservable to be used in CPS flow to get notify that cart is updated
   */
  public completeCartUpdated$: Observable<boolean>;
  /**
   * Emitter to emit fraud review in case of care
   */
  public isPaymentFraud: EventEmitter<boolean>;
  /**
   * boolean value that holds if we should show webfraudmessage
   */
  public showWebFraudMessage: boolean;
  /**
   * boolean value that holds if submit order API fails
   */
  public submitOrderError: boolean;
  /**
   * boolean value that holds if submit order API fails
   */
  public paymentFailure: boolean;
  /**
   * boolean value to disable place order CTA
   */
  public disablePlaceOrderCTA: boolean;
  /**
   * Subject to notify that submit order failed to publish
   */
  public publishOrderErrorMessage: Subject<boolean>;
  /**
   * Oservable to get notify that submit order failed to publish
   */
  public publishOrderErrorMessage$: Observable<boolean>;
  /**
   * Flag to check if user is auth/guest
   */
  public isAuthCustomer: boolean;
  /**
   * Emits to open scantocart modal
   */
  public openScanToCartModal: Subject<boolean>;
  /**
   * subject to trigger add, duplicate or remove a line event
   */
  // public addDuplicateRemoveLine: Subject<AddDuplicateRemoveLine>;

  private nTimesCartPDLIsBlocked: number;

  private fingerPrintId: string;

  public isDigitalCartTemplateEnabled: boolean;
  /**
   * flag for dormancy check for email verification
   */
  private isExistingEmailOtpEnabled: boolean;

  public promotionsCartLoaded: ReplaySubject<boolean>;

  public promotionsCartLoaded$: Observable<boolean>;

  public isAutoPayDiscountDisabled: boolean;
  constructor(
    private apiGatewayService: ApiGatewayService,
    private uIPermissionService: UIPermissionService,
    private windowRefService: WindowRefService,
    private appConfig: AppConfig,
    private stateStoreUtilsService: StateStoreUtilsService,
    private contentProviderService: ContentProviderService,
    private storeService: StoreService,
    private sharedBrowseService: SharedBrowseService,
    private globalErrorService: GlobalErrorService,
    private logger: LoggerService,
    private objectService: ObjectService,
    private tileManagementService: TileManagementService,
    private commonUtilService: CommonUtilService,
    private router: Router,
    private routeSnapshot: ActivatedRoute,
    private userService: UserService,
    private featureToggleUtilService: FeatureToggleUtilService,
    public pdlService: PdlService
  ) {
    super(pdlService, routeSnapshot);
    this.submitOrderErrorMessage = new BehaviorSubject<ErrorMessage[]>([]);
    this.cartUpdatedSource = new Subject();
    this.cartCountUpdatedSource = new Subject();
    this.ngUnsubscribe = new Subject<void>();
    this.cartUpdated$ = this.cartUpdatedSource.asObservable();
    this.cartCountUpdated$ = this.cartCountUpdatedSource.asObservable();
    this.completeCartUpdatedSource = new Subject();
    this.completeCartUpdated$ = this.completeCartUpdatedSource.asObservable();
    this.selectionDeviceUpdated = new Subject();
    this.goToCartSelectionUpdated$ = this.selectionDeviceUpdated.asObservable();
    this.planSelectedSource = new Subject();
    this.planSelected$ = this.planSelectedSource.asObservable();
    this.displayTaxes = new EventEmitter<boolean>();
    this.deviceAndSimToCartSource = new Subject();
    this.deviceAndSimToCart$ = this.deviceAndSimToCartSource.asObservable();
    this.productSelectorSpinnerSource = new Subject();
    this.productSelectorSpinner$ = this.productSelectorSpinnerSource.asObservable();
    this.cartTitleUpdate = new Subject<string>();
    this.currentPage = new Subject<string>();
    this.selectedShippingPrice = new Subject();
    this.selectedPrice$ = this.selectedShippingPrice.asObservable();
    this.auditData = new Subject();
    this.auditData$ = this.auditData.asObservable();
    this.maxEpinsReached = new EventEmitter<boolean>();
    this.cartAuthEmitter = new EventEmitter<boolean>();
    this.updateCart = new EventEmitter<boolean>();
    this.updateDeviceValue = false;
    this.currentMessage = new Subject<boolean>();
    this.currentMessage$ = this.currentMessage.asObservable();
    this.isPaymentFraud = new EventEmitter<boolean>();
    this.enableEmail = false;
    this.shippingWaiverSubject = new Subject<string>();
    this.shippingWaiverSubject$ = this.shippingWaiverSubject.asObservable();
    this.overrideTransactionIdSubject = new Subject<string>();
    this.overrideTransactionIdSubject$ = this.overrideTransactionIdSubject.asObservable();
    this.publishOrderErrorMessage = new Subject<boolean>();
    this.publishOrderErrorMessage$ = this.publishOrderErrorMessage.asObservable();
    this.disablePlaceOrderCTA = false;
    this.openScanToCartModal = new Subject();
    // this.addDuplicateRemoveLine = new Subject<AddDuplicateRemoveLine>();
    this.nTimesCartPDLIsBlocked = 0;
    this.isExistingEmailOtpEnabled = this.featureToggleUtilService.isExistingEmailOtpEnabled();
    this.isDigitalCartTemplateEnabled =
      this.featureToggleUtilService.isDigitalCartTemplateEnabled();
    this.promotionsCartLoaded = new ReplaySubject<boolean>();
    this.promotionsCartLoaded$ = this.promotionsCartLoaded.asObservable();
    this.isAutoPayDiscountDisabled = !this.appConfig.getConfig('autoPayDiscountEnabled');
  }

  public changeMessage(scroll: boolean): void {
    this.currentMessage.next(scroll);
  }

  public getHomePage(): string {
    const tppSession = sessionStorage.getItem('tpp_session');
    const newTppSession: TppSession = (tppSession && JSON.parse(tppSession)) || {};
    newTppSession.address = this.stateStoreUtilsService.getBillingAddress();
    sessionStorage.setItem('tpp_session', JSON.stringify(newTppSession));
    const brand = this.stateStoreUtilsService.getBrand();
    return this.appConfig.getConfig(`${brand}_path`) || '/';
  }

  /**
   * Removes the  accessory and makes delete API call
   * @param skuId: skuId of accessory
   * @return observable cart response
   */
  public removeAccessory(skuId: string): Observable<Cart> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      accessoryId: skuId.toString(),
    };
    return this.apiGatewayService
      .deleteRequest(API_MAP.API_deleteAccessory, null, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          this.announceCartUpdate(cartData);
          return cartData;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Checks if cart is empty
   * includeAccessory required. For web we display modal if cart is empty
   */
  public isCartNotEmpty(cart: Cart, includeAccessory: boolean = true): boolean {
    const cartStatus =
      cart &&
      !!this.objectService.get(cart, 'cartId') &&
      (this.objectService.get(cart, 'lines.length') > 0 ||
        this.objectService.get(cart, 'devices.length') > 0 ||
        this.objectService.get(cart, 'returningItem.length') > 0);
    if (includeAccessory) {
      return this.objectService.get(cart, 'accessories.length') > 0 || cartStatus;
    }
    return cartStatus;
  }

  public isNotP10MCartEmpty(cart: Cart, includeAccessory: boolean = true): boolean {
    const cartStatus =
      cart &&
      !!this.objectService.get(cart, 'cartId') &&
      (this.objectService.get(cart, 'devices.length') > 0 ||
        this.objectService.get(cart, 'returningItem.length') > 0);
    if (includeAccessory) {
      return this.objectService.get(cart, 'accessories.length') > 0 || cartStatus;
    }
    return cartStatus;
  }

  public isZeroDollarP10MSimsInCart(cart: Cart): boolean {
    // check that each device in the cart is at zero cost
    const devicesInCart = this.objectService.get(cart, `devices`) || [];
    return devicesInCart.length > 0
      ? devicesInCart.every((device: CartItem) => {
          return (
            this.objectService.get(device, 'price.priceSummary.payNowPrice.salePrice.amount') === 0
          );
        })
      : false;
  }

  public setNumberManagementMap(cartId: string, msisdnMap: NumberManagementOperations): void {
    this.stateStoreUtilsService.setNumberManagementMap(cartId, msisdnMap);
  }

  public getNumberManagementMap(cartId: string): NumberManagementOperations {
    return this.stateStoreUtilsService.getNumberManagementMap(cartId);
  }

  public clearNumberManagementMap(cartId: string): void {
    if (this.stateStoreUtilsService.getNumberManagementMap(cartId)) {
      this.stateStoreUtilsService.clearNumberManagementMap(cartId);
    }
  }

  /**
   * Checks if service is in error state
   * @return CartItem
   */
  public checkIfServiceInErrorState(): CartItem[] {
    // const isRapChannel = SETTINGS.CHANNEL.toLowerCase() === 'nrd';
    // const isRetail = this.commonUtilService.isRetailChannel();
    const linesData = this.objectService.get(this.stateStoreUtilsService.getCart(), 'lines') || [];
    let filteredLinesData;
    // if (isRapChannel) {
    //   filteredLinesData = linesData;
    // }
    // else {
    const focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
    const lineId = focusedTileIdDetails.lineId;
    // const lineId = isRetail ? this.stateStoreUtilsService.getFocusedTileId() : focusedTileIdDetails.lineId;
    filteredLinesData = linesData.filter((cartLine: CartItem) => {
      return cartLine.id === lineId;
    });
    // }
    const selectedLineServices = this.objectService.get(filteredLinesData, '0.services') || [];
    const servicesInFocus = selectedLineServices.filter((service: CartItem) => {
      return (
        service.cartItemStatus === HARDGOODS_CONSTANTS.ERROR &&
        service.statusCode === HARDGOODS_CONSTANTS.REMOVE
      );
    });
    return servicesInFocus;
  }

  /**
   * Method to set the flag to show/ hide profile info section
   * @returns boolean
   */
  public setEnableEmail(enableEmail: boolean): void {
    this.enableEmail = enableEmail;
  }

  /**
   * Method to get the flag to show/ hide profile info section
   * @returns boolean
   */
  public getEnableEmail(): boolean {
    return this.enableEmail;
  }

  /**
   * Function to return the EIP eligibility of a selected device
   */
  public getProductEipEligibility(product: CartItem): boolean {
    let offerId: string;
    let upc: string;

    // TODO: Mock data is bringing upc codes in randomly as
    // numbers or strings. Force casting to string.
    if (product) {
      offerId = this.objectService.get(product, 'offerId');
      upc = String(this.objectService.get(product, 'upc'));
    }

    const creditLevel = this.stateStoreUtilsService.getCreditLevel();
    const productDetail = this.stateStoreUtilsService.getProductDetails(offerId);

    if (productDetail) {
      const itemDetail = productDetail.skus.find((item: Sku) => item.skuCode === upc);
      const isCreditRun = this.sharedBrowseService.checkCustomerCredit();
      let eipOption;
      if (itemDetail) {
        if (isCreditRun) {
          const creditClass = this.stateStoreUtilsService.getCustomerCreditClass();
          eipOption = itemDetail.skuPrices.find(
            (option: SkuPrice) => option.pricingOptionName === creditClass
          );
        } else if (creditLevel !== 'NONE') {
          eipOption = itemDetail.skuPrices.find(
            (option: SkuPrice) => option.pricingOptionName === creditLevel
          );
        }
      }
      if (eipOption) {
        return true;
      }
    }
    return false;
  }

  /**
   * Method to get cart from backend
   * @returns {Observable<Cart>}
   */
  public getBackendCart(cartId: string = ''): Observable<Cart> {
    const pathParams = {
      cartId: cartId ? cartId : this.stateStoreUtilsService.getCartId(),
    };
    return this.apiGatewayService
      .getRequest(API_MAP.API_2131_retrieveCart, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // this.announceCartUpdate(response.json().cart);
          // tslint:disable-next-line: no-string-literal
          return response?.body['cart'];
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Get OrderDetails
   * @return Observable<Order>
   */
  public getOrderReviewDetails(orderId?: string): Observable<OrderSummaryInterface> {
    // const isRap = (SETTINGS.CHANNEL.toLocaleLowerCase() === 'nrd');
    const cartId = localStorage.getItem('cartId');
    const pathParams = {
      orderId: orderId ? orderId : this.stateStoreUtilsService.getCartId(),
      // orderId: orderId ? orderId : isRap ? cartId : this.stateStoreUtilsService.getCartId()
    };
    const payload = {
      accountId: this.stateStoreUtilsService.getAccountId() || '',
      sendOrderDetailsOnly: true,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2814_getOrderDetails, payload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const orderDetails = response?.body;
          return orderDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method is used to verify is there any fruad detected if yes then perform action accordingly
   * @returns void
   */
  public checkFraudVerification(): void {
    const interval = 45000;
    this.getBackendCart()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (cartResponse: Cart) => {
          if (cartResponse.fraudCheckStatus.name === ORDER_MODULE_CONSTANT.REVIEW) {
            this.fraudApiTimeout = this.windowRefService.nativeWindow().setTimeout(() => {
              this.checkFraudVerification();
            }, interval);
          } else if (cartResponse.fraudCheckStatus.name === ORDER_MODULE_CONSTANT.ACCEPT) {
            this.fraudDetectionSource.next(false);
          } else if (cartResponse.fraudCheckStatus.name === ORDER_MODULE_CONSTANT.REJECT) {
            this.fraudDetectionSource.next(true);
          }
        },
        (error: APIError) => {
          this.fraudDetectionSource.next(true);
        }
      );
    this.windowRefService.nativeWindow().clearTimeout(this.fraudApiTimeout);
  }

  /**
   * Method to deletes all current cart selections
   */
  public deleteCart(savecartId?: string): Observable<void> {
    const cartId = savecartId ? savecartId : this.stateStoreUtilsService.getCart().cartId;
    const pathParams = { cartId: cartId };
    return this.apiGatewayService
      .deleteRequest(API_MAP.API_2121_deleteCart, null, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          // @TODO: When delete is successful we get 204 response
          const emptyCart: Cart = new ShoppingCart();
          this.stateStoreUtilsService.deleteCart();
          this.stateStoreUtilsService.clearRAPSession();
          this.announceCartUpdate(emptyCart);
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method to cancel cart
   * @param {string} removeCart
   */
  public cancelCart(removeCart?: any): Observable<void> {
    const cartId = this.stateStoreUtilsService.getCart().cartId;
    const queryParams = removeCart ? { removeCart: removeCart } : null;
    const pathParams = { cartId: cartId };
    return this.apiGatewayService
      .deleteRequest(API_MAP.API_2174_cancelCheckoutOrder, null, null, queryParams, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          this.stateStoreUtilsService.setCartCheckedOutStatusFlag(false);
          if (removeCart !== 'false') {
            const emptyCart: Cart = new ShoppingCart();
            this.stateStoreUtilsService.deleteCart();
            this.announceCartUpdate(emptyCart);
          }
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method used to cancel cart during fraud review
   * @returns void
   */
  public cancelFraudReviewOrder(): Observable<void> {
    const cartId = this.stateStoreUtilsService.getCart().cartId;
    const requestData = {
      orderId: cartId,
      isPostVoid: false,
      orderStatus: '',
      reasonCode: ACTION_CODE_CONSTANTS.CANCEL_REASON_CODE,
    };
    const pathParam = {
      orderId: cartId,
    };

    return this.apiGatewayService
      .postRequest(API_MAP.API_2815_postCancelOrder, requestData, null, null, pathParam)
      .pipe(
        map((response: HttpResponse<CancelOrderDetails>) => {
          const emptyCart: Cart = new ShoppingCart();
          this.stateStoreUtilsService.deleteCart();
          this.announceCartUpdate(emptyCart);
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Updates Accessory
   * @param {CartItem | Product} accessory
   * @param {string | number} accessoryQty
   * @param {string} priceType
   * @returns {Observable<Cart>}
   */
  public updateAccessory(
    accessory: CartItem | Product,
    accessoryQty: string | number,
    priceType?: string
  ): Observable<Cart> {
    const accessoryPayload: UpdateAccessory = {
      quantity: Number(accessoryQty),
      pricingOption: priceType ? priceType : (<CartItem>accessory).selectedPricingOption,
      offerId: accessory.offerId,
      sku: accessory.defaultSku,
    };
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      accessoryId: (<CartItem>accessory).upc || (<Product>accessory).defaultSku,
    };
    return this.apiGatewayService
      .putRequest(API_MAP.API_2105_updateAccessory, accessoryPayload, null, null, pathParams)
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          this.announceCartUpdate(cart);
          return cart;
        }),
        catchError((error: Error) => observableThrowError(error))
      );
  }

  /**
   * Function to update device to current line
   * @param {string} priceOption
   * @param {string} offerId
   * @param {string} sku
   * @returns {Observable<Cart>}
   */
  public updateLineDevice(
    sku?: string,
    offerId?: string,
    isUpgrade?: boolean,
    isRemorseExchange?: boolean,
    payload?: RemorseExchangeRequestPayload,
    lineID?: string,
    overrideIndicator?: string,
    isUsku?: boolean,
    cartScheduleStatus?: string,
    isAllowEdit?: boolean
  ): Observable<Cart> {
    let lineId =
      lineID ||
      this.stateStoreUtilsService.getFocusedTileId() ||
      this.stateStoreUtilsService.getLineId();
    let requestPayload;
    if (isUpgrade) {
      lineId = this.stateStoreUtilsService.getSelectedUpgradeLineId();
    }
    if (isRemorseExchange) {
      requestPayload = payload;
    } else {
      requestPayload = {
        sku: sku,
        offerId: offerId,
        pricingOption: 'FULL',
      };
      if (isUpgrade) {
        const propTrType = 'transactionTypes';
        const propAccId = 'accountId';
        const propCustId = 'customerId';
        requestPayload[propTrType] = ORDER_TYPE.PURCHASEEQUIPMENT;
        requestPayload[propAccId] = this.stateStoreUtilsService.getAccountId();
        requestPayload[propCustId] = this.stateStoreUtilsService.getBscsCustomerID();
      }
    }
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId,
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator);
    }
    this.cartScheduleStatus(requestPayload, cartScheduleStatus, isUsku, isAllowEdit);
    return this.apiGatewayService
      .postRequest(API_MAP.API_2111_addDeviceToLine, requestPayload, header, null, pathParams)
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          // if (!this.commonUtilService.isRetailChannel()) {
          this.setLineItemsInStateStore(cart, lineId);
          // }
          this.stateStoreUtilsService.setCartId(cart.cartId);
          this.announceCartUpdate(cart);
          return cart;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * initializes API 2218 to enable Shipping price override
   * @param hasPermission: boolean
   * @param route: string
   * @param screenId: string
   * @param overrideup: string
   * @param overridedown: string
   * @param userName: string
   * @param password: string
   * @returns cartOverrideresponse
   */
  public shippingPriceOverride(
    hasPermission: boolean,
    route: string,
    screenId: string,
    overrideup: string | number,
    overridedown: string | number,
    userName?: string,
    password?: string
  ): Observable<ShippingOverridePriceResponse> {
    let reqObj;
    reqObj = {
      screenDetails: {
        route: route,
        screenId: screenId,
        Elements: [overrideup, overridedown],
      },
    };
    if (!hasPermission) {
      reqObj.userId = userName;
      reqObj.password = password;
    }
    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_2218_postManagerOverrideRequest, reqObj, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response?.body;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }

  /**
   * initializes API 2156
   * @param hasPermission: boolean
   * @param isSimkit: boolean
   * @param hasSimKitPriceOverridePermissions: boolean
   * @param screenDetail: ScreenDetail
   * @param newPrice: string
   * @param reason: string
   * @param cartId: string
   * @param transactionType: string
   * @param cartItemId: string
   * @param userName: string
   * @param password: string
   * @returns cartOverrideresponse
   */
  public cartPriceOverride(
    hasPermission: boolean,
    isSimkit: boolean,
    hasSimKitPriceOverridePermissions: boolean,
    screenDetails: ScreenDetails,
    newPrice: string,
    reason: string,
    cartItemId: string,
    cartId?: string,
    transactionType?: string,
    userName?: string,
    password?: string
  ): Observable<Cart> {
    let reqObj;
    reqObj = {
      amount: newPrice,
      reason: reason,
      cartItemId: cartItemId,
    };
    if (cartId) {
      reqObj.cartId = cartId;
    }
    if (screenDetails) {
      reqObj.screenDetails = screenDetails;
    }
    if (transactionType) {
      reqObj.transactionTypes = [transactionType];
    }
    if (!hasPermission || (isSimkit && !hasSimKitPriceOverridePermissions)) {
      reqObj.supervisorId = userName;
      reqObj.password = password;
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2156_overridePrice, reqObj, null, null)
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          this.stateStoreUtilsService.setCart(cart);
          return cart;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }

  /**
   * @param {string} deviceId
   * @returns {Observable<Cart>}
   */
  public updateCartLevelDeviceToLineLevel(
    paramDeviceId: string,
    paramSku: string,
    lineId?: string,
    newDeviceSku?: string,
    replacementOfferId?: string,
    transactionType?: string,
    usageContext?: string,
    overrideIndicator?: string
  ): Observable<Cart> {
    const requestPayload = {
      replacementSku: newDeviceSku,
      replacementOfferId: replacementOfferId,
      transactionTypes: transactionType,
      context: usageContext,
    };
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: lineId ? lineId : this.stateStoreUtilsService.getFocusedTileId(),
      deviceId: paramDeviceId,
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator);
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2142_convertToLineItem, requestPayload, header, null, pathParams)
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          this.stateStoreUtilsService.setCart(cart);
          this.stateStoreUtilsService.setCartId(cart.cartId);
          this.announceCartUpdate(cart);
          return cart;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * If customer selected i don't need a plan after selecting plan,move line level device to cart level.
   * @param {string} deviceId
   * @returns {Observable<Cart>}
   */
  public updateLineLevelDeviceToCartLevel(
    deviceId: string,
    lineId?: string,
    replacementSku?: string,
    replacementOfferId?: string,
    transactionType?: string,
    usageContext?: string,
    overrideIndicator?: string
  ): Observable<Cart> {
    const requestPayload = {
      replacementSku: replacementSku,
      replacementOfferId: replacementOfferId,
      transactionTypes: transactionType,
      context: usageContext,
    };
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: lineId || this.stateStoreUtilsService.getFocusedTileId(),
      deviceId,
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator);
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2143_convertToNonLineItem, requestPayload, header, null, pathParams)
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          this.stateStoreUtilsService.setCart(cart);
          this.stateStoreUtilsService.setCartId(cart.cartId);
          this.announceCartUpdate(cart);
          return cart;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * @returns Observable of BrowseEntry
   * Method sets default customer information if customer has not selected any information
   * prior to opening the cart page
   */
  public setDefaultCartState(): Observable<BrowseEntry> {
    let customerConfigData: BrowseEntry;
    let storeData: StoreData;
    const savedCustomerConfig = this.stateStoreUtilsService.getCustomerConfigData();
    this.sharedBrowseService.getUserInputsConfigData();
    if (!savedCustomerConfig) {
      customerConfigData = {
        creditLevel: CreditLevelEnum.EXCELLENT_CREDIT,
        billingType: BillingTypeEnum.POSTPAID,
        zip: '',
        firstName: '',
        lastName: '',
        middleName: '',
        personalContactNumber: '1234',
        email: '',
        isDefault: true,
      };
      return this.storeService.getStoreDetails().pipe(
        map((storeState: boolean) => {
          storeData = this.storeService.getStoreOpsData();
          // customerConfigData.zip = '98007';
          customerConfigData.isDefault = true;
          // TODO uncomment when mocks are replaced
          // customerConfigData.zip = storeData.address.zip.slice(0, zipCodeLength) || '98007';
          this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);

          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' returning default customerConfigData in setDefaultCartState() in ' +
              CART_SUPPORT_SERVICE
          );

          return customerConfigData;
        })
      );
    } else {
      this.logger.debug(
        CART_MODULE_COMPONENTS.CART_MODULE,
        CART_SUPPORT_SERVICE,
        ' returning saved customerConfigData in setDefaultCartState() in ' + CART_SUPPORT_SERVICE
      );

      return observableOf(savedCustomerConfig);
    }
  }

  /**
   * @returns cart object
   * Returns saved cart information from state store
   */
  public getStatestoreCartReference(): Cart {
    return this.stateStoreUtilsService.getCart();
  }

  /**
   * Checks if device in cart is backordered
   * @memberof CartSupportService
   */
  public checkBackorderedDevice(): boolean {
    const cartResponse = this.getStatestoreCartReference();
    return (
      this.checkBackorderedLineDevice(this.objectService.get(cartResponse, 'lines')) ||
      this.checkBackorderedNonlineDevice(this.objectService.get(cartResponse, 'devices')) ||
      this.checkBackorderedNonlineDevice(this.objectService.get(cartResponse, 'accessories'))
    );
  }

  /**
   * Function to get availability status
   * @returns void
   */
  public getAvailabilityStatus(): boolean {
    const cartResponse = this.getStatestoreCartReference();
    const linesData = this.objectService.get(cartResponse, 'lines', []);
    const devicesData = this.objectService.get(cartResponse, 'devices', []);
    const accessoriesData = this.objectService.get(cartResponse, 'accessories', []);
    return (
      this.estimatedShipDateForLineDevice(linesData) ||
      this.estimatedShipDateForNonlineDevice(devicesData) ||
      this.estimatedShipDateForNonlineDevice(accessoriesData)
    );
  }

  /**
   * Checks if line device in cart is backordered
   * @param lines
   * @memberof CartSupportService
   */
  public estimatedShipDateForLineDevice(lines: Line[]): boolean {
    const estimatedShipDateLineDevice = lines.find((line: Line) => {
      return (
        this.objectService.get(line, 'device.availability') === CART_REVIEW_CONST.NOT_AVAILABLE ||
        this.objectService.get(line, 'device.availability') === CART_REVIEW_CONST.BACKORDERED
      );
    });
    return !!estimatedShipDateLineDevice;
  }

  /**
   * Checks if nonLine device in cart is backordered
   * @param devices
   * @memberof CartSupportService
   */
  public estimatedShipDateForNonlineDevice(devices: CartItem[]): boolean {
    const estimatedShipDateNonLineDevice = devices.find((device: CartItem) => {
      return (
        this.objectService.get(device, 'availability') === CART_REVIEW_CONST.NOT_AVAILABLE ||
        this.objectService.get(device, 'availability') === CART_REVIEW_CONST.BACKORDERED
      );
    });
    return !!estimatedShipDateNonLineDevice;
  }

  /**
   * Checks if line device in cart is backordered
   * @param lines
   * @memberof CartSupportService
   */
  public checkBackorderedLineDevice(lines: Line[]): boolean {
    const backOrderedLineDevice = lines.find((line: Line) => {
      return this.objectService.get(line, 'device.availability') === CART_REVIEW_CONST.BACKORDERED;
    });
    return !!backOrderedLineDevice;
  }

  /**
   * Checks if nonLine device in cart is backordered
   * @param devices
   * @memberof CartSupportService
   */
  public checkBackorderedNonlineDevice(devices: CartItem[]): boolean {
    const backOrderedNonlineDevice = devices.find((device: CartItem) => {
      return this.objectService.get(device, 'availability') === CART_REVIEW_CONST.BACKORDERED;
    });
    return !!backOrderedNonlineDevice;
  }

  /**
   * Function to get options for select override and reason code drop down
   * @returns SelectItem[]
   */
  public getOptions(optionString: string): SelectItem[] {
    const valueArray = JSON.parse(optionString);
    return this.commonUtilService.createDropDownData(valueArray);
  }

  /**
   * Provides the  updated cart data
   * @param cartUpdate: cart data
   * @return void
   */
  public announceCartUpdate(cartUpdate: Cart): void {
    if (cartUpdate) {
      this.extractAndCacheBillingAddressListFromCart(cartUpdate);
      this.stateStoreUtilsService.setCart(cartUpdate);
      this.stateStoreUtilsService.setCartId(this.objectService.get(cartUpdate, `cartId`));
      this.stateStoreUtilsService.setAutoPayFlag(cartUpdate.autoPayDiscIntent === FLAG_STATUS.YES);
      this.updatePDLData(cartUpdate);
      // FIXME: Adeed timeout to resolve cart announcement issue change detection issue on subjects
      this.cartUpdatedSource.next(true);
    }
  }

  /**
   * function to extract billing address from cart
   */
  public extractAndCacheBillingAddressListFromCart(cart: Cart): void {
    if (!this.stateStoreUtilsService.getAddFan()) {
      const billingAddressList = this.objectService
        .get(cart, 'addressList', [])
        .filter((address: AddressList) => {
          return (
            address.usageContext.includes(CART_ADDRESS_USAGE.ADDRESS_USAGE_BILLING) &&
            this.objectService.get(address, 'addressCommunication', {}).postalCode &&
            address.id
          );
        });
      if (billingAddressList.length > 0) {
        const currentBillingAddress = this.stateStoreUtilsService.getBillingAddress();
        const billingAddress =
          billingAddressList.filter((billingAddress: Address) => {
            return (
              billingAddress.addressLine1 === currentBillingAddress.addressLine1 &&
              billingAddress.postalCode === currentBillingAddress.postalCode
            );
          })[0] ||
          billingAddressList.filter((billingAddress: Address) => {
            return billingAddress.postalCode === currentBillingAddress.postalCode;
          })[0] ||
          this.objectService.get(cart, 'addressList.0', {});
        if (billingAddress.id && billingAddress.addressCommunication) {
          const address: Address = billingAddress.addressCommunication;
          address.addressid = billingAddress.id;
          this.stateStoreUtilsService.setBillingAddress(address);
        }
      }
    }
  }

  /**
   * Announce GoToCart Selection Update
   * @param cartSelectionStateUpdate: boolean
   * @return void
   */
  public announceGoToCartSelectionUpdate(cartSelectionStateUpdate: boolean): void {
    this.selectionDeviceUpdated.next(cartSelectionStateUpdate);
  }

  /**
   * Provides the  updated cart data
   * @return void
   */
  public announceProductSelectorSpinnerUpdate(value: boolean): void {
    this.productSelectorSpinnerSource.next(value);
  }

  /**
   * Retrieves AEM content for the browse-services components
   * @param url of page
   * @return Observable<BrowseServiceContent>
   */
  public getAEMContent(url?: string): Observable<Object> {
    return this.contentProviderService.getContentData(url, 'shared', true).pipe(
      map((aemData: Object) => {
        const contentData = {
          configurableData: null,
          authorableData: null,
        };
        contentData.configurableData = <ConfigData>(
          this.contentProviderService.getConfigurableData(aemData)
        );
        contentData.authorableData = this.contentProviderService.getAuthorableData(aemData);
        return contentData;
      })
    );
  }

  /**
   * Retrieves AEM content for the cart authentication components
   * @param url of page
   * @return Observable<BrowseServiceContent>
   */
  public getCartAuthenticationContent(url?: string): Observable<CartAuthenticationAEMData> {
    return this.contentProviderService.getContentData(url).pipe(
      map((response: Response) => {
        return <CartAuthenticationAEMData>this.contentProviderService.getConfigurableData(response);
      })
    );
  }

  /**
   * Add item to cart and makes post cart 2119 API call
   * @param sku: sku id of item
   * @param offerId: offerid of item
   * @param selectedQty: qty of item
   * @return observable cart response
   */
  public addToCart(
    sku: string,
    offerId: string,
    selectedQty: number,
    pricingOption?: string
  ): Observable<Cart> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      // cartId: 1004
    };
    const addToCartRequest: Api2119Request = {
      sku: sku,
      offerId: offerId,
      quantity: selectedQty,
      pricingOption: pricingOption ? pricingOption : PRICING_TYPE.FRP,
    };
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2119_addAccessoriesToCart,
        addToCartRequest,
        headers,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<Cart>) => {
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            'received response for addToCart() in ' +
              CART_SUPPORT_SERVICE +
              '  pathParam:' +
              JSON.stringify(pathParams) +
              ' || addToCartRequest: ' +
              JSON.stringify(addToCartRequest) +
              ' || response: ' +
              JSON.stringify(response)
          );
          // tslint:disable:no-string-literal
          this.announceCartUpdate(response?.body['cart']);
          return response?.body['cart'];
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for postRequest addToCart() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );

          return observableThrowError(error);
        })
      );
  }

  /**
   * Add promo code to cart
   * @param promoCode: promo code
   */
  public applyPromoCode(promoCode: string, cartId: string = ''): Observable<boolean | Cart> {
    const cart = this.stateStoreUtilsService.getCart();
    const applyPromoCodeRequest: AddPromoCodeRequest = {
      promotionCode: promoCode,
      transactionTypes: [TRANSACTION_TYPE.ACTIVATION],
    };
    cartId = cartId ? cartId : cart.cartId;
    const headers = new HttpHeaders();
    // @TODO: pass this header with actual DCP APIs. Mocks are not supporting this header
    // headers.append('follow', 'true');
    return this.apiGatewayService
      .postRequest(API_MAP.API_2114_applyPromotions, applyPromoCodeRequest, headers, null, {
        cartId: cartId,
      })
      .pipe(
        map((response: HttpResponse<Cart>) => {
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            'received response for applyPromoCode() in ' +
              CART_SUPPORT_SERVICE +
              ' response: ' +
              JSON.stringify(response)
          );

          const promoCodeResponse = response?.body['cart'];
          if (promoCodeResponse) {
            this.announceCartUpdate(promoCodeResponse);
            return promoCode === SHARED_CONSTANTS.DUMMY_CART_PROMO ? promoCodeResponse : true;
          } else {
            return false;
          }
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for postRequest applyPromoCode() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );

          return observableThrowError(error);
        })
      );
  }

  /**
   * Add promo code to cart
   * @param promoCode: promo code
   */
  public removePromoCode(promoCode: string): Observable<boolean> {
    const cart = this.stateStoreUtilsService.getCart();
    const cartItemId = this.objectService.get(cart, `pricing.subTotal.promotions.0.promoId`);
    const pathParams = {
      cartItemId: cartItemId,
      promotionCode: promoCode,
    };

    const headers = new HttpHeaders();
    // @TODO: pass this header with actual DCP APIs. Mocks are not supporting this header
    // headers.append('follow', 'true');
    return this.apiGatewayService
      .postRequest(API_MAP.API_2114_applyPromotions, pathParams, headers, null, {
        cartId: cart.cartId,
      })
      .pipe(
        map((response: HttpResponse<Cart>) => {
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            'received response for removePromoCode() in ' +
              CART_SUPPORT_SERVICE +
              '  pathParam:' +
              JSON.stringify(pathParams) +
              ' || response: ' +
              JSON.stringify(response)
          );

          const promoCodeResponse = response?.body;
          if (promoCodeResponse) {
            this.announceCartUpdate(promoCodeResponse);
            return true;
          } else {
            return false;
          }
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for deleteRequest removePromoCode() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );

          return observableThrowError(error);
        })
      );
  }

  /**
   * Function used to create line with no plan and add device to line
   * @param {string} sku
   * @param {string} offeringId
   * @param {string} transactionTypes
   */
  public createLineWithNoPlan(
    sku: string,
    productOfferingId: string,
    lineId: string,
    transactionTypes: string,
    simNumber: string,
    overrideIndicator?: boolean
  ): Observable<Cart> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator.toString());
    }
    const requestObject = {
      sku,
      productOfferingId,
      lineId,
      transactionTypes,
      simNumber: simNumber || undefined,
      customerId: this.stateStoreUtilsService.getBscsCustomerID() || undefined,
      accountId: this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId() || undefined,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2104_createLineAndAddDevice, requestObject, header, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          this.stateStoreUtilsService.setCartId(cartData.cartId);
          this.stateStoreUtilsService.setCart(cartData);
          this.announceCartUpdate(cartData);
          return cartData;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }

  /**
   * Make a post request to add a device to cart.
   * @param skuNum string
   * @param offerId string
   * @param updateTileModel number
   * @param quantity string
   * @param isUpgrade string
   * @param isRemorseExchange string
   * @param payload RemorseExchangeRequestPayload
   * @param cartItemId string
   * @param overrideIndicator string
   */
  public postDeviceAsNonLineItem(
    skuCollection: AddSkuItemsToNonLineInterface[] = [],
    updateTileModel?: boolean,
    isRemorseExchange?: boolean,
    payload?: RemorseExchangeRequestPayload,
    overrideIndicator?: string,
    isUsku?: boolean,
    cartScheduleStatus?: string,
    isAllowEdit?: boolean
  ): Observable<Cart> {
    // @TODO stop sending transactionTypes after EMS remove the dependency
    let requestObject;
    const accId =
      this.stateStoreUtilsService.getAccountId() === CART_REVIEW_CONST.ANON
        ? ''
        : this.stateStoreUtilsService.getAccountId();
    if (updateTileModel && skuCollection.length > 0) {
      // While updating on PDP screen there will not be multiple items at this point. So we are taking the 1st object.
      skuCollection[0].cartItemId = this.objectService.get(
        this.stateStoreUtilsService.getFocusedTileLineNonLineItem(),
        'selectedDevice.cartItemId'
      );
      requestObject = {
        transactionTypes: ORDER_TYPE.PURCHASEEQUIPMENT,
        skuDetails: skuCollection,
        customerId: this.stateStoreUtilsService.getBscsCustomerID(),
        accountId: this.stateStoreUtilsService.getAddFan() ? '' : accId,
      };
      // TODO: Handling multiple Quantity design discussion pending
    } else if (this.objectService.get(skuCollection, `${0}.quantity`)) {
      requestObject = {
        skuDetails: skuCollection,
        transactionTypes: ORDER_TYPE.PURCHASEEQUIPMENT,
        customerId: this.stateStoreUtilsService.getBscsCustomerID(),
        accountId: this.stateStoreUtilsService.getAddFan() ? '' : accId,
      };
    } else if (isRemorseExchange) {
      requestObject = payload;
    } else {
      requestObject = {
        skuDetails: skuCollection,
        customerId: this.stateStoreUtilsService.getBscsCustomerID(),
        accountId: this.stateStoreUtilsService.getAddFan() ? '' : accId,
        transactionTypes: ORDER_TYPE.PURCHASEEQUIPMENT,
      };
    }
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator);
    }
    this.cartScheduleStatus(requestObject, cartScheduleStatus, isUsku, isAllowEdit);
    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2107_addingDeviceToNonLineItem,
        requestObject,
        header,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          if (isRemorseExchange) {
            this.setNonLineItemsInStateStore(cartData);
          }
          this.stateStoreUtilsService.setCartId(cartData.cartId);
          this.stateStoreUtilsService.setCart(cartData);
          this.announceCartUpdate(cartData);
          this.stateStoreUtilsService.setCartEditFlag(true);
          return cartData;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * checks if only device is selected and no plan on cart page
   */
  public isOnlyDevice(): boolean {
    const cartResponse = this.getStatestoreCartReference();
    let isLineDevice;
    if (cartResponse) {
      for (const line of cartResponse.lines) {
        if (!isLineDevice) {
          isLineDevice = !!this.objectService.get(line, 'device.offerName');
        }
      }
    }
    return !!this.objectService.get(cartResponse, 'devices.0') || isLineDevice;
  }

  /**
   * checks if device exists
   */
  public deviceInCart(): void {
    let devices = [];
    const cartResponse = this.getStatestoreCartReference();
    this.objectService.get(cartResponse, 'lines', []).forEach((line: Line) => {
      if (
        this.objectService.get(line, 'device.productSubType.0', CART_REVIEW_CONST.SIMCARD) !==
        CART_REVIEW_CONST.SIMCARD
      ) {
        devices.push(line.device);
      }
    });
    const availableDevices = [];
    this.objectService.get(cartResponse, 'devices', []).forEach((item: CartItem) => {
      if (this.objectService.get(item, 'productSubType.0') !== CART_REVIEW_CONST.SIMCARD) {
        availableDevices.push(item);
      } else if (item.offerId === HARDGOODS_CONSTANTS.TOURIST_SIM_KIT) {
        this.isTouristSimkit = true;
      }
    });
    devices = [...devices, ...availableDevices];
    switch (devices.length) {
      case 0:
        this.productName = undefined;
        this.moreThanOneDevice = false;
        this.productExists = false;
        break;
      case 1:
        this.productName = this.objectService.get(devices, '0.offerName');
        this.moreThanOneDevice = false;
        this.productExists = true;
        break;
      default:
        this.productName = undefined;
        this.moreThanOneDevice = true;
        this.productExists = true;
        break;
    }
  }

  /**
   * Function to validate imei and sim
   * @param requestPayload
   * @return ValidateImeiAndSim[]
   */
  public validateImeiAndSim(
    requestPayload: ValidateImeiAndSimRequest[]
  ): Observable<ValidateImeiAndSim[]> {
    let headers = new HttpHeaders();
    const timestamp = new Date().toISOString();
    headers = headers.append('timestamp', timestamp.toString());
    return this.apiGatewayService
      .postRequest(API_MAP.API_2168_validateImeiAndSim, requestPayload, headers, null, null)
      .pipe(
        map((response: HttpResponse<ValidateImeiAndSim[]>) => {
          return response?.body;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }
  /**
   * This will store CartNonLineItem which will be used for validation
   * @param {Cart} cartData
   */
  private setNonLineItemsInStateStore(cartData: Cart): void {
    const replacementCount = this.objectService.get(cartData, `replacementItem.length`);
    if (replacementCount > 0) {
      const replacementItemInFocus = this.objectService.get(
        cartData,
        `replacementItem.${replacementCount - 1}`
      );
      const cartNonLineItem: CartNonLineItem = {
        offerId: replacementItemInFocus.offerId,
        id: replacementItemInFocus.id,
        defaultSku: replacementItemInFocus.sku,
        imei: replacementItemInFocus.imei,
        productSubtype: replacementItemInFocus.productSubtype,
        repEnteredSku: this.stateStoreUtilsService.getManualEnteredSku(),
      };

      this.stateStoreUtilsService.clearManualEnteredSku();
      if (this.objectService.get(replacementItemInFocus, `productType.0`) === 'Device') {
        this.stateStoreUtilsService.setNonLineItems(cartNonLineItem.id, cartNonLineItem);
      } else {
        this.stateStoreUtilsService.setNonLineItems(cartNonLineItem.offerId, cartNonLineItem);
      }
    }
  }

  /**
   * This will store CartLineItem which will be used for validation
   * @param {Cart} cartData
   * @param {string} lineId
   */
  private setLineItemsInStateStore(cartData: Cart, lineId: string): void {
    const replacementItemInFocus = cartData.lines.find((line: CartLine) => {
      return line.id === lineId;
    });

    const cartLineItem: ReplacementItem = {
      id: this.objectService.get(replacementItemInFocus, `id`),
      offerId: this.objectService.get(replacementItemInFocus, `offerId`),
      defaultSku: this.stateStoreUtilsService.getManualEnteredSku(),
      imei: this.objectService.get(replacementItemInFocus, `imei`),
      repEnteredSku: this.stateStoreUtilsService.getManualEnteredSku(),
    };

    this.stateStoreUtilsService.setLineItems(lineId, cartLineItem);
  }

  public setLineItems(lineId: string, cartLineItem: CartLineItem): void {
    this.stateStoreUtilsService.setLineItems(lineId, cartLineItem);
  }

  public setNonLineItems(offerId: string, nonLineItem: CartNonLineItem): void {
    this.stateStoreUtilsService.setNonLineItems(offerId, nonLineItem);
  }

  public getLineItems(lineId: string): CartLineItem {
    return this.stateStoreUtilsService.getLineItems(lineId);
  }

  public getNonLineItems(lineId: string): CartNonLineItem {
    return this.stateStoreUtilsService.getNonLineItems(lineId);
  }
  /**
   * Make a post request to add a device to cart.
   */
  public getExistingLineDetails(): Observable<FAN> {
    const payload = {};
    const isCartNotEmpty = this.isCartNotEmpty(this.stateStoreUtilsService.getCart());
    const isAddAFan = this.stateStoreUtilsService.getAddFan();
    if (
      isCartNotEmpty ||
      isAddAFan ||
      this.stateStoreUtilsService.getTileIdList(lineTypeEnum.LINE).length > 0
    ) {
      return observableOf(null);
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2038_getExistingLines, payload, null, null, null)
      .pipe(
        map((response: HttpResponse<FAN>) => {
          // tslint:disable-next-line: no-string-literal
          const existingLinesResponse = response?.body['financialAccount'];
          this.stateStoreUtilsService.setCart(<Cart>existingLinesResponse);
          this.stateStoreUtilsService.setIsHardStop(existingLinesResponse.isHardStop);
          const cartLines: Line[] = this.objectService.get(existingLinesResponse, 'lines');
          for (
            let index = 0;
            index < this.objectService.get(existingLinesResponse, 'linesOccupied');
            index++
          ) {
            const tempLine = this.objectService.get(cartLines, `${index}`);
            this.tileManagementService.createTile(lineTypeEnum.EXISTING, false, tempLine, null);
          }
          const currentStatus = {
            isApiCalled: true,
            totalCount: this.objectService.get(existingLinesResponse, 'linesOccupied'),
          };
          this.stateStoreUtilsService.setExistingLineStatus(currentStatus);
          // if (!!this.commonUtilService.isRetailChannel()) {
          //   this.stateStoreUtilsService.setExistingLinesValue(this.objectService.get(existingLinesResponse, 'linesOccupied', 0));
          // }
          return existingLinesResponse;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * Delete cart level device.
   * Sku is used to remove the tile associted to the device.
   */
  public deleteCartLevelDevice(id?: string, updateTileModel?: boolean): Observable<Cart> {
    return this.apiGatewayService
      .deleteRequest(API_MAP.API_2122_deleteCartLevelDevice, null, null, null, {
        cartId: this.stateStoreUtilsService.getCartId(),
        deviceId: id,
      })
      .pipe(
        // tslint:disable-next-line: no-string-literal
        map((response: HttpResponse<Cart>) => response?.body['cart']),
        map((cart: Cart) => {
          this.tileManagementService.deleteLine();
          this.tileManagementService.setNonLineItemDetails(cart);
          this.stateStoreUtilsService.setCart(cart);
          this.stateStoreUtilsService.setCartId(cart.cartId);
          this.announceCartUpdate(cart);
          this.stateStoreUtilsService.setCartEditFlag(true);
          return cart;
        }),
        catchError((error: APIError) => observableThrowError(error))
      );
  }

  /**
   * This method is used to put the selected plan in the cart using API-2109
   * @param userPlanConfigId : stores the id of plan configId
   * @param lineNumber : line number for which the plan needs to be updated in cart
   * @param isProductSelector : boolean flag to identify if it is a product selector page
   * @param transactionTypes : Transaction type that will be passed in API request
   * @param accountId : Account id that will be passed in API request
   * @param overrideIndicator : Boolean flag to identify if override
   * @param isBillingAddressConflict : flag to identify if Billing address conflict scenario
   * @returns Observable<PlanModel>
   */
  // @TODO remove focusedTileIdDetails after soc was removed
  // tslint:disable-next-line:cyclomatic-complexity
  public putSelectedPlanToCart(
    userPlanConfigId?: string,
    lineNumber?: string,
    isProductSelector?: boolean,
    transactionTypes?: string,
    accountId?: string,
    focusedTileIdDetails?: LineDeivceDetails,
    overrideIndicator?: boolean,
    isBillingAddressConflict?: boolean
  ): Observable<CartResponse> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId() || HARDGOODS_CONSTANTS.CARTID,
    };
    const data: UpdatePlansRequest = {
      planConfigurationsId: userPlanConfigId,
      lineNumber: lineNumber,
      transactionTypes: transactionTypes,
    };
    this.getSimNumber(data, focusedTileIdDetails, isProductSelector);
    if (isBillingAddressConflict) {
      const newBillingAddress: Address = this.stateStoreUtilsService.getBillingAddress();
      data.addressId = newBillingAddress.addressid;
      data.sequenceNumber = newBillingAddress.sequenceId;
    }
    if (accountId) {
      data.accountId = accountId;
    } else {
      data.accountId = this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId();
    }
    let headers = new HttpHeaders();
    if (overrideIndicator) {
      headers = headers.append('overrideIndicator', overrideIndicator.toString());
    }
    data.customerId = this.stateStoreUtilsService.getBscsCustomerID() || '';
    return this.apiGatewayService
      .putRequest(API_MAP.API_2109_updatePlansToCart, data, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<CartResponse>) => {
          if (response && response?.body) {
            const cartData: Cart = response?.body.cart;
            if (!this.isAuthCustomer) {
              cartData.lines.sort((a, b) => +a.id[a.id.length - 1] - +b.id[b.id.length - 1]);
            }
            this.stateStoreUtilsService.setServiceZeroDollarFlag(false);
            this.stateStoreUtilsService.setCart(cartData);
            this.stateStoreUtilsService.setCartId(cartData.cartId);
            this.announceCompleteCartUpdate(response);
            this.announceCartUpdate(cartData);
            this.setPhoenixCookie({cartItemCount: 1});
            return response?.body;
          }
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  private isAutoPayEnrolled(): boolean {
    const billingDetails = this.stateStoreUtilsService.getBillingBlockDetails();
    const autoPayDetails = this.stateStoreUtilsService.getAutoPayDetails();
    return autoPayDetails ? autoPayDetails.autoPay : (billingDetails ? billingDetails.autoPay : false);
  }

  public showAutoPayDiscountCartBanner(isCPS: boolean): boolean {
    const cartOrderType = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'orderType'
    );
    if (this.isAutoPayDiscountDisabled || 
      ![ORDER_TYPE.MANAGERATEPLAN,
        ORDER_TYPE.ADDALINE,
        ORDER_TYPE.ACTIVATION].includes(cartOrderType) || 
      (!this.checkIfDueNowAmount() && isCPS))
        return false;
    const autoPay = this.isAutoPayEnrolled();
    const eligiblePlansInCart = this.stateStoreUtilsService.getAutoPayDiscountEligiblePlansInCart();
    const inCart = !!eligiblePlansInCart.inCart;
    const isAddLine = (ORDER_TYPE.ADDALINE == cartOrderType);
    const inActivation = ORDER_TYPE.ACTIVATION == cartOrderType;
     if (this.checkIfCustomerAuthenticated()) {
      if (this.stateStoreUtilsService.getAddFan() && (isAddLine || inActivation)) {
        return inCart;
      } else if (isAddLine || (ORDER_TYPE.MANAGERATEPLAN == cartOrderType)) {
        if (autoPay) {
          return inCart && !eligiblePlansInCart.inAccount;
        } else {
          return inCart;
        }
      }
    } else if (inActivation) {
      return inCart
    }
  }

  /**
   * method to assign sim number in request
   */
  private getSimNumber(
    data: UpdatePlansRequest,
    focusedTileIdDetails: LineDeivceDetails,
    isProductSelector: boolean
  ): void {
    // const isRapChannel = SETTINGS.CHANNEL.toLowerCase() === 'nrd';
    // const simNumber = isRapChannel ? this.stateStoreUtilsService.getValidatedSim()
    const simNumber = this.objectService.get(focusedTileIdDetails, 'byodSimData.simNumber');
    // if ((isProductSelector && simNumber && simNumber !== '') || (isRapChannel && simNumber)) {
    if (isProductSelector && simNumber && simNumber !== '') {
      data.simNumber = simNumber;
    }
  }

  public setByosData(): void {
    // const tileId = this.stateStoreUtilsService.getFocusedTileId();
    // const focusedLineDetails = this.stateStoreUtilsService.getLineAndDeviceTiles().
    //   filter((x: LineAndDeviceTile) => x.id === tileId);
    // if (this.objectService.get(focusedLineDetails, '0.planSelected.id')) {
    //   const focusedTileLineNonLineItem = this.stateStoreUtilsService.getFocusedTileLineNonLineItem();
    //   focusedTileLineNonLineItem.isLineSetupCompleted = true;
    //   this.stateStoreUtilsService.setFocusedTileLineNonLineItem(focusedTileLineNonLineItem, tileId);
    // }
  }

  /**
   * Function to duplicate line
   */
  public duplicateExistingLine(): Observable<Cart> {
    const focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
    const serviceThreshold = this.objectService.get(
      focusedTileIdDetails,
      'deviceDetails.serviceThreshold'
    );
    const newLineNumber = this.tileManagementService.generateTile(lineTypeEnum.LINE, true).tileId;
    this.stateStoreUtilsService.setServiceThresholdValue(newLineNumber, serviceThreshold);
    const requestPayload = {
      existingLineNumber: focusedTileIdDetails.tileId,
      newLineNumber,
    };
    const cartId = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2126_duplicateLineInCart, requestPayload, null, null, cartId)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const cartData = this.objectService.get(response?.body, 'cart');
          this.announceCartUpdate(cartData);
          this.stateStoreUtilsService.setCartId(this.objectService.get(cartData, 'cartId'));
          const previousLineDetails = this.stateStoreUtilsService.getFocusedTileLineNonLineItem();
          this.tileManagementService.createTile();
          const newLineDetails = this.stateStoreUtilsService.getFocusedTileLineNonLineItem();
          if (this.objectService.get(previousLineDetails, 'byodCMMOrImei')) {
            newLineDetails.byodCMMOrImei = {
              isSkipSelected: true,
            };
          }
          this.stateStoreUtilsService.setFocusedTileLineNonLineItem(newLineDetails);
          return cartData;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function to delete line
   */
  public deleteFocusedLine(lineId?: string): Observable<Cart> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: lineId ? lineId : this.stateStoreUtilsService.getFocusedTileId(),
    };
    return this.apiGatewayService
      .deleteRequest(API_MAP.API_2106_deleteLine, null, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          this.announceCartUpdate(cartData);
          return cartData;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Returns the latest device that was added to cart.
   *
   * Based on the assumption that it is appended as the last item.
   */
  public getAddedDevice(cart: Cart, sku: string): CartItem {
    const devices = this.objectService.get(cart, 'devices', []);
    const accessories = this.objectService.get(cart, 'accessories', []);
    const cartNonLineItems = devices.concat(accessories);
    const addedSku = cartNonLineItems.filter((cartItem: CartItem) => {
      return cartItem.sku === sku;
    });
    const addedDevice = addedSku && addedSku.length > 0 ? addedSku.pop() : undefined;
    return addedDevice;
  }

  /**
   * This method creates an array of accessories based on the quantity of an accessory
   * @param cartData cart Response
   * @return accessory data bades on quantity
   */
  public getAccessoriesByQuantity(cartData: Cart): CartItem[] {
    const accessoriesByQuantity = [];
    if (cartData && cartData.accessories) {
      for (const accessory of cartData.accessories) {
        for (let quantity = 0; quantity < accessory.quantity; quantity++) {
          accessoriesByQuantity.push(accessory);
        }
      }
    }
    return accessoriesByQuantity;
  }

  /**
   * This method gathers information for the request object for post product API call and returns it
   */
  public getPayloadForPostProduct(sku: string): ProductRequest {
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    const billType = customerConfigData
      ? this.sharedBrowseService.getBillingTypeAPIMapping(customerConfigData.billingType)
      : 'POSTPAID';
    const zip = customerConfigData ? customerConfigData.zip : '98007';
    // const isCareChannel = SETTINGS.CHANNEL.toLowerCase() === 'care';
    const channelSegment = {
      // salesChannelCode: isCareChannel ? 'Over the Phone' : 'In-Person',
      // salesSubChannelCode: isCareChannel ? 'Care' : 'Full Service',
      // subChannelCategory: isCareChannel ? 'Frontline' : 'T-MobileRetail'
      salesChannelCode: 'In-Person',
      salesSubChannelCode: 'Full Service',
      subChannelCategory: 'T-MobileRetail',
    };
    return {
      accessoryCategory: '',
      associationTypes: [],
      catalogProductTypes: [],
      requestedSkus: [sku],
      city: '',
      creditLevels: [],
      customerId: '',
      productFamilyIds: [],
      isDetailRequest: true,
      facets: [],
      filtersRequested: [],
      filterOptionsSelected: [],
      productTypes: [],
      productSubTypes: [],
      channelSegment: channelSegment,
      customerSegments: [],
      billingType: billType,
      salesChannels: [],
      accountSegments: [],
      transactionTypes: [],
      postalCode: zip,
      storeId: '4328',
      offerTypes: [OFFER_CONSTANTS.BASE],
      offerSubTypes: [OFFER_CONSTANTS.HARDGOODS],
      sortOptions: [
        {
          sortDirection: HARDGOODS_CONSTANTS.ASCENDING,
          sortOptionValue: HARDGOODS_CONSTANTS.RELEVANCE,
        },
      ],
      pageSize: '100',
      pageNumber: '1',
      crpIds: [],
      tacCodes: [],
      relationTypes: [],
      dealerCodes: [],
      masterDealerCodes: [],
      state: '',
    };
  }

  /**
   * This method post the user language prefernce "English / Spanish"
   * If it's a device, then make the API call to add the device as a new line item
   * @param itemFormControl
   * @param lineId string
   */
  public postAuditRecords(
    formData: {
      [key: string]: string;
    },
    lineId: string,
    contextType?: string
  ): Observable<object> {
    const auditContextRecords = [];
    this.createAuditRecord(formData, lineId, auditContextRecords);
    return this.postAuditRecordsApi(auditContextRecords);
  }

  /**
   * Submit the order for the particular cartId
   * @param {OrderPaymentModel} paymentDetails
   * @param {OnlineSubmitOrder} submitOrder
   * @returns {Observable<Cart>}
   */
  public submitOrder(paymentDetails?: OrderPaymentModel): Observable<Cart> {
    let payload;
    if (paymentDetails) {
      const paymentRequestPayload = {};
      payload = {
        actionCode: ORDER_ACTION_TYPE.ADD_SUBMIT,
        reqPayload: paymentRequestPayload,
      };
    }
    return this.commonSubmitOrder(payload);
  }

  /**
   * checks for cart error
   * @param {SubmitOrderResponse} order response
   * @param {string} actionCode
   * @param {string} actionCode
   * @returns {boolean}
   */
  private checkIfRefundFlow(
    submitOrderResponse: SubmitOrderResponse,
    actionCode: string,
    transactionType: string
  ): boolean {
    return (
      actionCode === SHARED_PAYMENT.SUBMIT &&
      (transactionType === SHARED_PAYMENT.RETURN ||
        transactionType === SHARED_PAYMENT.REFUND ||
        transactionType === SHARED_PAYMENT.EXCHANGE) &&
      this.validatePaymentErrors(submitOrderResponse)
    );
  }

  public commonSubmitOrder(
    payload: object,
    headers: HttpHeaders = new HttpHeaders()
  ): Observable<SubmitOrderResponse> {
    const billingType = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus().toLowerCase();
    headers.set('billingType', billingType);
    const actionCode = this.objectService.get(payload, 'actionCode');
    const cart = this.stateStoreUtilsService.getCart();
    return new Observable((subscriber: Subscriber<SubmitOrderResponse>) => {
      // const isRap = SETTINGS.CHANNEL.toLowerCase() === 'nrd';
      const cartId = localStorage.getItem('cartId');
      let pathParams;
      // if (isRap) {
      //   pathParams = {
      //     cartId: this.objectService.get(payload, 'cartId')
      //   };
      // } else {
      pathParams = {
        cartId: this.objectService.get(cart, 'cartId'),
      };
      // }
      // const apiToCall = !isRap ? API_MAP.API_3150_submitOrder : API_MAP.API_2150_submitOrder;
      const apiToCall = API_MAP.API_3150_submitOrder;
      return this.apiGatewayService
        .postRequest(apiToCall, payload, headers, null, pathParams, null, false)
        .pipe(
          map((response: HttpResponse<Object>) => {
            return response?.body;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        )
        .subscribe(
          (submitOrderResponse: SubmitOrderResponse) => {
            this.stateStoreUtilsService.setCartCheckedOutStatusFlag(true);
            if (
              this.isValidPayment(submitOrderResponse, actionCode) &&
              !this.checkIfRefundFlow(
                submitOrderResponse,
                this.objectService.get(payload, 'actionCode'),
                this.objectService.get(payload, 'transactionType')
              )
            ) {
              this.submitOrderErrorMessage.next([]);
              this.stateStoreUtilsService.clearAssessmentProductsMap();
              this.stateStoreUtilsService.setAutoPayFlag(false);
              if (this.isExistingEmailOtpEnabled) {
                this.stateStoreUtilsService.clearEmailVerificationCache();
              }
              if (this.objectService.get(submitOrderResponse, 'messages')) {
                this.stateStoreUtilsService.setSubmitOrderResponseMessages(
                  this.objectService.get(submitOrderResponse, 'messages')
                );
              }
              subscriber.next(submitOrderResponse);
            } else {
              if (
                this.objectService.get(payload, 'actionCode') === ORDER_ACTION_TYPE.ADD_SUBMIT &&
                this.objectService
                  .get(submitOrderResponse, 'responseCode', '')
                  .toLocaleLowerCase() === SHARED_PAYMENT.DECLINE
              ) {
                const removeCart = 'false';
                this.cancelCart(removeCart)
                  .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
                  .subscribe(() => {});
              }
              this.submitOrderErrorMessage.next(
                this.filterSubmitOrderErrorMessage(
                  this.objectService.get(submitOrderResponse, 'errorMessage', [])
                )
              );
              subscriber.error(submitOrderResponse);
            }
            subscriber.complete();
            return subscriber;
          },
          (errors: APIError[]) => {
            this.stateStoreUtilsService.setCartCheckedOutStatusFlag(true);
            const errorMessage = [];
            this.disablePlaceOrderCTA = this.globalErrorService.checkErrorSubStatusCode(errors);
            errors.forEach((error: APIError) => {
              if (this.objectService.get(error, 'subStatusCode') !== PUBLISH_ERROR_CODE) {
                errorMessage.push({ uiMessage: error.userMessage });
              }
              if (this.objectService.get(error, 'subStatusCode') === CHECKOUT_ERROR_CODE) {
                const removeCart = 'false';
                this.cancelCart(removeCart)
                  .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
                  .subscribe(() => {});
              }
              this.validateErrorCode(error.subStatusCode);
            });
            this.submitOrderErrorMessage.next(errorMessage);
            subscriber.error(errors);
            subscriber.complete();
          }
        );
    });
  }

  /**
   * Method use to create 2150 request
   * @param {boolean} bypassFraudCheck
   * @param {boolean} bypassInventoryCheck
   * @returns {OnlineSubmitOrder}
   * @memberof CartSupportService
   */
  public createSubmitOrderRequest(
    bypassFraudCheck?: boolean,
    bypassInventoryCheck?: boolean,
    isAcceptable?: boolean
  ): OnlineSubmitOrder {
    const paymentDetails: OrderPaymentModel = this.stateStoreUtilsService.getOrderPayment();
    const cart = this.stateStoreUtilsService.getCart();
    const amount = this.objectService.get(cart, 'pricing.total.payNowPrice.salePrice.amount');
    const fingerPrintId = this.getDFPDetails();
    let submitOrder;
    if (paymentDetails && paymentDetails.cardDetails && cart) {
      paymentDetails.cardDetails.addressId = paymentDetails.addressid;
      submitOrder = new OnlineSubmitOrder(
        cart.cartId,
        cart.orderType,
        amount,
        paymentDetails.cardDetails,
        paymentDetails.cardAddressDetails,
        null,
        bypassFraudCheck,
        bypassInventoryCheck,
        isAcceptable,
        fingerPrintId
      );
    } else {
      submitOrder = new OnlineSubmitOrder(
        cart.cartId,
        cart.orderType,
        amount,
        null,
        null,
        cart.orderReason,
        bypassFraudCheck,
        bypassInventoryCheck,
        isAcceptable,
        fingerPrintId
      );
    }
    return submitOrder;
  }

  public getDFPDetails(): string {
    // if ((SETTINGS.CHANNEL.toLowerCase() ===  CHANNELS.WEB) &&
    //     this.stateStoreUtilsService.getDFPflag()) {
    if (this.stateStoreUtilsService.getDFPflag()) {
      return this.stateStoreUtilsService.getSessionId();
    } else {
      return null;
    }
  }

  /**
   * Method use to filter backorder items from api 2150
   * @param {OrderLine} orderLines
   * @returns {OrderLine}
   * @memberof CartSupportService
   */
  public filterBackorderedItems(orderLines: OrderLine[]): OrderLine[] {
    const backOrderedLines = orderLines.filter((orderLine: OrderLine) => {
      return (
        this.objectService.get(orderLine, 'inventoryStatus.statusCode') ===
        HARDGOODS_CONSTANTS.BACKORDER
      );
    });
    return backOrderedLines;
  }

  public filterSubmitOrderErrorMessage(errorMessages: ErrorMessage[] = []): ErrorMessage[] {
    const backOrderedLines = errorMessages.filter((errorMessage: ErrorMessage) => {
      return (
        this.objectService.get(errorMessage, 'uiMessage') &&
        this.objectService.get(errorMessage, 'uiShortMessageCode') &&
        this.objectService.get(errorMessage, 'uiShortMessageCode') !== 'approval'
      );
    });
    return backOrderedLines;
  }

  /**
   * Method use to create submit order request for return
   * @returns {OnlineSubmitOrder}
   * @memberof CartSupportService
   */
  public createReturnSubmitOrderRequest(): OnlineSubmitOrder {
    let submitOrder;
    const cart = this.stateStoreUtilsService.getCart();
    const amount = this.objectService.get(cart, 'pricing.total.refundLaterPrice.salePrice.amount');
    submitOrder = new OnlineSubmitOrder(cart.cartId, cart.orderType, amount);
    let paymentMethod = null;
    if (amount) {
      paymentMethod = {
        paymentId: this.objectService.get(cart, 'payment.0.receivedPaymentId'),
        paymentMethodCode: this.objectService.get(cart, 'payment.0.paymentMethodCode'),
        transactionType: SHARED_PAYMENT.REFUND,
      };
    }
    this.objectService.set(submitOrder, 'paymentMethod', paymentMethod);
    return submitOrder;
  }

  /**
   * This method post data of audit records to backened Api 2116
   * @param auditContextRecords audit context records
   * @return object <Observable>
   */
  public postAuditRecordsApi(auditContextRecords: object[]): Observable<Object> {
    const requestPayload = {
      auditContextRecords: auditContextRecords,
    };
    const headers: HttpHeaders = new HttpHeaders();
    // @TODO - below hardcoded line id will be removed once api is ready
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: '123',
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2116_postAuditRecords, requestPayload, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            `received response in postAuditRecords() ${CART_SUPPORT_SERVICE}`,
            ` pathParams: ${pathParams}`,
            ` || auditRecordRequest: ${JSON.stringify(requestPayload)}` ||
              `response: ${JSON.stringify(response)}`
          );
          return response?.body;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for postRequest for postAuditRecords() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   * This method creates audit request payload object
   * @param itemFormControl
   * @param lineId string
   * @param auditContextRecords object[]
   */
  public createAuditRecord(
    formData: { [key: string]: string },
    lineId: string,
    auditContextRecords: object[]
  ): void {
    const lineNumber: string = this.stateStoreUtilsService.getFocusedTileId();
    for (const itemField in formData) {
      if (formData[itemField]) {
        const auditRecord = [];
        const context = this.getContext(itemField);
        auditRecord.push(
          {
            'audit-key': 'lineNumber',
            'audit-value': lineNumber,
          },
          {
            'audit-key': itemField,
            'audit-value': formData[itemField],
          }
        );
        auditContextRecords.push({
          auditContext: context,
          auditRecords: auditRecord,
        });
      }
    }
  }

  /**
   * Function used to return audit context
   * @param contextType of type string
   * @returns contextType of type string
   */
  public getContext(contextType: string): string {
    let auditContext = '';
    switch (contextType) {
      case AUDIT_CONTEXT.LANGUAGE: {
        auditContext = AUDIT_CONTEXT.AUDIT_PREF_LANG;
        break;
      }
      case AUDIT_CONTEXT.SIM: {
        auditContext = AUDIT_CONTEXT.AUDIT_SIM;
        break;
      }
      case AUDIT_CONTEXT.IMEI: {
        auditContext = AUDIT_CONTEXT.AUDIT_IMEI;
        break;
      }
      case AUDIT_CONTEXT.SKU: {
        auditContext = AUDIT_CONTEXT.AUDIT_SKU;
        break;
      }
      default: {
      }
    }
    return auditContext;
  }

  /**
   * Function updates line name and sends update for cart data change
   * @param newLineName newLine name
   */
  public updateLineName(newLineName: string): Observable<Cart> {
    const tileIdInFocus = this.stateStoreUtilsService.getTileIdInFocus();
    const tile: Tile = this.stateStoreUtilsService.getTileModelForTileId(tileIdInFocus);
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: tile.lineId,
    };
    const data = {
      lineName: newLineName,
    };
    return this.apiGatewayService
      .putRequest(API_MAP.API_2118_toUpdateLineName, data, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          const res = response?.body;
          this.announceCartUpdate(res);
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            `received response in postAuditRecords() in ${CART_SUPPORT_SERVICE}`,
            ` pathParams: ${pathParams}`,
            ` || response.json().cart: ${JSON.stringify(res)}`
          );
          return res;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for putRequest for updateLineName() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );

          return observableThrowError(error);
        })
      );
  }

  /**
   * Get Partial Complete Line
   * open Modal to navigate Cart or delete partial lines
   */
  public getPartialLines(): string[] {
    const partialTiles = [];
    const activeTiles = this.stateStoreUtilsService.getActiveTiles();
    if (activeTiles) {
      activeTiles.forEach((tileId: string) => {
        const tileModel = this.stateStoreUtilsService.getTileModelForTileId(tileId);
        if (tileModel && tileModel.lineId) {
          const line: CartLine = this.getLineFromCart(tileModel.lineId);
          const cartLineItem = CartUtils.getCartItemAsArrayFromLine(
            line,
            CART_ITEM_STATUS.ITEM_ADD,
            'plan'
          );
          const plan = cartLineItem && cartLineItem.length > 0 ? cartLineItem[0] : null;
          const hasDevice = this.objectService.get(line, 'device.offerId');
          const hasPlan = this.objectService.get(plan, 'offerId');
          const hasBYOD = this.objectService.get(line, 'simKit.sku');
          // @TODO: Enhance for BYOS
          if (!(hasDevice || hasBYOD) || !hasPlan) {
            partialTiles.push(tileId);
          }
        }
      });
    }
    return partialTiles;
  }

  public announcePlanSelection(planConfigurationId: string): void {
    this.planSelectedSource.next(planConfigurationId);
  }

  // @TODO following function being called at navbar component
  /**
   * Delete All Partial Lines
   * Update cart on every deletion
   *
   */
  public deleteAllPartialLines(partialLines: string[]): Observable<CartItem> {
    const deleteLineArrayObser = [];
    let deleteBundleConcat: Observable<CartResponse> = null;
    partialLines.forEach((tileId: string) => {
      this.removeTileFromActiveTiles(tileId);
      const tileModel = this.stateStoreUtilsService.getTileModelForTileId(tileId);
      const lineId = tileModel && tileModel.lineId;
      const pathParams = {
        cartId: this.stateStoreUtilsService.getCartId(),
        lineId: lineId,
      };
      let headers = new HttpHeaders();
      headers = headers.append('follow', '');
      deleteLineArrayObser.push(
        this.apiGatewayService
          .deleteRequest(API_MAP.API_2106_deleteLine, null, headers, null, pathParams)
          .pipe(
            map((resp: HttpResponse<Object>) => {
              // this.stateStoreUtilsService.setActiveLines(activeLines);
              return resp?.body;
            })
          )
      );
    });
    deleteBundleConcat = deleteLineArrayObser[0];
    const lineLen = deleteLineArrayObser.length;
    for (let observerIndex = 1; observerIndex < lineLen; observerIndex++) {
      deleteBundleConcat = deleteBundleConcat.pipe(concat(deleteLineArrayObser[observerIndex]));
    }
    return deleteBundleConcat.pipe(
      last(),
      map((cartResponse: CartResponse) => {
        this.announceCartUpdate(cartResponse.cart);
        return cartResponse.cart;
      })
    );
  }

  /**
   * Builds the active tiles array from tile models
   */
  public buildActiveTilesArray(activeTiles: string[]): Tile[] {
    const tileArray: Tile[] = [];
    for (const tileId of activeTiles) {
      const tile = this.stateStoreUtilsService.getTileModelForTileId(tileId);
      tileArray.push(tile);
    }
    return tileArray;
  }

  /**
   * Service Bundle For All Complete Lines
   * update cart object in statestore
   */
  public serviceBundleForCompleteLines(): Observable<CartItem> {
    const serviceBundleArrayObser = [];
    let serviceBundleConcat: Observable<Cart> = null;
    const completeLines = this.getCompleteLines();
    if (completeLines) {
      completeLines.forEach((tileId: string) => {
        const serviceBundle: EligibleServices = this.stateStoreUtilsService.getServices(tileId);
        const serviceBundleId =
          serviceBundle && serviceBundle.services && serviceBundle.services.id
            ? serviceBundle.services.id
            : '';
        // Filter selected service offer Id
        const line: CartLine = this.getLineFromCart(tileId);
        const isServiceDelta = this.compareWithCartServices(serviceBundle, line);
        if (isServiceDelta && serviceBundleId) {
          serviceBundleArrayObser.push(this.postServiceBundle(serviceBundleId, line.id));
        }
      });
    }
    if (serviceBundleArrayObser && serviceBundleArrayObser.length > 0) {
      serviceBundleConcat = serviceBundleArrayObser[0];
      const serviceLen = serviceBundleArrayObser.length;
      for (let observerIndex = 1; observerIndex < serviceLen; observerIndex++) {
        serviceBundleConcat = serviceBundleConcat.pipe(
          concat(serviceBundleArrayObser[observerIndex])
        );
      }
      return serviceBundleConcat.pipe(last());
    } else {
      return observableOf(null);
    }
  }

  /**
   * Function to format anonymous line names
   * @param index number
   */
  public lineTextFormatter(index: number): string {
    const SMALLEST_DOUBLE_DIGIT = 10;
    if (this.isDigitalCartTemplateEnabled) {
      return index < SMALLEST_DOUBLE_DIGIT
        ? 'line '.concat('0').concat(index.toString())
        : 'line '.concat(index.toString());
    } else {
      return index < SMALLEST_DOUBLE_DIGIT
        ? 'Line '.concat('0').concat(index.toString())
        : 'Line '.concat(index.toString());
    }
  }

  /**
   * Post API call Service Bundle
   * @returns Observable<Cart>
   */
  public postServiceBundle(
    id: string,
    lineId: string,
    overrideIndicator?: boolean,
    isCPS?: boolean
  ): Observable<Object> {
    let transactionType;
    const cartOrderType = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'orderType'
    );
    if (isCPS) {
      if (cartOrderType !== ORDER_TYPE.MANAGERATEPLAN) {
        transactionType = ORDER_TYPE.ADDONSERVICES;
      } else {
        transactionType = ORDER_TYPE.MANAGERATEPLAN;
      }
    } else {
      transactionType = cartOrderType;
    }
    const accountId = this.stateStoreUtilsService.getAccountId();
    const serviceBundlePayload: Object = {
      id: id,
      accountNumber: this.stateStoreUtilsService.getAddFan() ? '' : accountId ? accountId : '',
      customerId: this.stateStoreUtilsService.getBscsCustomerID() || '',
      transactionTypes: [transactionType],
    };

    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId() || 'CARTID',
      lineId: lineId,
    };
    let headers = new HttpHeaders();
    if (overrideIndicator) {
      headers = headers.append('overrideIndicator', overrideIndicator.toString());
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2112_serviceBundle, serviceBundlePayload, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<Cart>) => {
          this.logger.debug(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            `received response for postServiceBundle() in ${CART_SUPPORT_SERVICE}`,
            ` response: ${response}`
          );
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          cartData.responseStatus = response?.body['status'];
          this.announceCartUpdate(cartData);
          return cartData;
        }),
        catchError((error: APIError[]) => {
          this.logger.error(
            CART_MODULE_COMPONENTS.CART_MODULE,
            CART_SUPPORT_SERVICE,
            ' API error for postRequest for postServiceBundle() in ' +
              CART_SUPPORT_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error[0].code
          );

          return observableThrowError(error);
        })
      );
  }

  /**
   * Fetches the modal content
   * @return removeModal
   */
  public clearCartModalObject(
    aemContentData: ConfigurableCartAEMData,
    isTertiary?: boolean
  ): StaticModal {
    const clearCartModalData: StaticModal = {
      headerText: '',
      bodyText: '',
      disableDismiss: false,
      disableEsc: false,
      modalName: 'clear-cart-modal',
      buttons: [
        {
          id: aemContentData.continue,
          type: CPS_CONSTANTS.SUBMIT,
          name: aemContentData.continue,
          class: CPS_CONSTANTS.PRIMARY_CLASS,
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
    };
    clearCartModalData.headerText = isTertiary
      ? this.objectService.get(aemContentData, 'areYouSure')
      : this.objectService.get(aemContentData, 'changesWillBeLost');
    clearCartModalData.bodyText = isTertiary
      ? this.objectService.get(aemContentData, 'doYouWishToContinue')
      : this.objectService.get(aemContentData, 'continueCancellation');
    if (isTertiary) {
      clearCartModalData.buttons.unshift({
        id: aemContentData.backButton,
        type: CPS_CONSTANTS.CANCEL_BTN,
        name: aemContentData.backButton,
        class: CPS_CONSTANTS.SECONDARY_BTN,
      });
    }
    return clearCartModalData;
  }

  /**
   * Method to create change effective date modal
   * @param content: AEM content to get text values
   * @returns StaticModal
   */
  public createChangeEffectiveDateModal(content: ConfigurableCartAEMData): StaticModal {
    const modal: StaticModal = {
      headerText: content.changeSelectedDateModalHeader,
      bodyText: content.changeSelectedDateModalText,
      disableDismiss: true,
      disableEsc: true,
      buttons: [
        {
          id: CART_LANDING_CONSTANTS.CONTINUE_BTN,
          type: 'submit',
          name: content.continue,
          class: 'btn btn-primary',
        },
        {
          id: CART_LANDING_CONSTANTS.CLEAR_CART_BTN,
          type: 'submit',
          name: content.clearCart,
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
    return modal;
  }

  /**
   * Function looks through lines in cart and returns line with matching lineId
   * @param lineId lineId to retrieve from cart
   */
  public getLineFromCart(lineId: string): CartLine {
    const lines = this.objectService.get(this.stateStoreUtilsService.getCart(), 'lines');
    if (lines) {
      return lines.find((line: Line) => {
        return line.id && line.id === lineId;
      });
    } else {
      return null;
    }
  }

  /**
   * Function generates a new random ID which is not already in use
   */
  public generateRandomTileID(): string {
    const randomNumberMultiplier = 100;
    const newId = 'Tile_' + Math.floor(randomNumber() * randomNumberMultiplier).toString();
    const activeTiles = this.stateStoreUtilsService.getActiveTiles();
    if (activeTiles) {
      const idAlreadyInUse = activeTiles.find((tileId: string) => {
        return tileId === newId;
      });
      return idAlreadyInUse ? this.generateRandomTileID() : newId;
    } else {
      return newId;
    }
  }

  /**
   * Function to check the eligibilty of the plans
   * @return boolean
   */
  public isPlanEligibilityExceeded(): boolean {
    const cartData = this.stateStoreUtilsService.getCart();
    const maxLinesData = this.stateStoreUtilsService.getMaxLineDetails();
    let tablentLineCounts: number = 0;
    let handsetLineCounts: number = 0;
    let totalLineCounts: number = 0;

    if (cartData && this.objectService.get(cartData, 'lines') && cartData.lines.length) {
      cartData.lines.forEach((line: CartLine) => {
        if (line.lineType === PRODUCT_SELECTOR_CONSTANTS.HANDSET) {
          handsetLineCounts++;
        } else if (line.lineType === PRODUCT_SELECTOR_CONSTANTS.TABLET) {
          tablentLineCounts++;
        }
      });
    }

    totalLineCounts = tablentLineCounts + handsetLineCounts;
    return totalLineCounts === maxLinesData.maxLines;
  }

  public validateSIMNumber(simNumber: string, override?: boolean): Observable<ValidateSimResponse> {
    const headers = new HttpHeaders();
    const queryParam = {
      simnumber: simNumber,
    };
    if (override) {
      this.objectService.set(queryParam, 'overrideProfile', true);
    }
    console.log(queryParam);
    return this.apiGatewayService
      .postRequest(API_MAP.API_2900_validateSim, queryParam, headers)
      .pipe(
        map((response: HttpResponse<Object>) => {
          if (response) {
            this.stateStoreUtilsService.setSimValidation(response?.body);
            return response?.body;
          }
        }),
        catchError((error: APIError) => {
          this.logger.error('ByodModule', 'ByodLandingService', error.systemMessage, error.code);
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function to validate Imei
   * @param DeviceData
   * @returns ImeiCheckInterface
   */
  public validateDevice(queryParams: DeviceData): Observable<ImeiCheckInterface> {
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .postRequest(API_MAP.API_2170_getCompatibilityCheck, queryParams, headers)
      .pipe(
        map((response: HttpResponse<Object>) => {
          if (response) {
            this.stateStoreUtilsService.setImeiCompatibilityCheck(response?.body);
            return response?.body;
          }
        }),
        catchError((error: APIError) => {
          this.logger.error('ByodModule', 'ByodLandingService', error.systemMessage, error.code);
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function to checkout cart
   * @param cartResponse of type cart
   * @returns Cart
   */
  public checkoutCart(
    cartResponse: Cart,
    transactionType?: string,
    hazmatPayload?: HazmatPayloadInterface
  ): Observable<Cart> {
    const cartId = cartResponse.cartId;
    const payload: CheckoutCart = {
      phoneNumber: this.stateStoreUtilsService.getEpinMsisdn(),
      transactionTypes: [],
      isEpinRedeemed: this.stateStoreUtilsService.getEpinRedemption(),
    };
    if (transactionType) {
      payload.transactionTypes.push(transactionType);
    }
    if (hazmatPayload) {
      payload.hazmatIndicator = hazmatPayload.hazmatIndicator;
      payload.returnShipmentEligible = hazmatPayload.returnShipmentEligible;
      payload.colorPrinterIndicator = hazmatPayload.colorPrinterIndicator;
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2120_postCheckOutCart, payload, null, null, { cartId: cartId })
      .pipe(
        map((response: HttpResponse<Cart>) => {
          this.submitOrderErrorMessage.next([]);
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          this.announceCartUpdate(cartData);
          return cartData;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Used to determine to load instore or shipping payment page
   */
  public navigateToPayment(): void {
    // if (this.commonUtilService.isRetailChannel()) {
    //   const fulfilmentOption: ShipTo = this.stateStoreUtilsService.getShipFulfillment();
    //   if (fulfilmentOption && fulfilmentOption.shipTo) {
    //     this.router.navigate([PAYMENT_ROUTES.DEFAULT]);
    //     return;
    //   }
    // }
    this.router.navigate([PAYMENT_ROUTES.INSTORE]);
  }

  /**
   * method to check if retail activation flow is to be loaded
   * @returns boolean
   */
  public shouldNavigateToPlanServices(
    isShipToFlow: boolean = false,
    scanToCart: boolean = false
  ): boolean {
    let isP10mCustomer: boolean = false;
    isShipToFlow = this.objectService.get(
      this.stateStoreUtilsService.getShipFulfillment(),
      'shipTo',
      isShipToFlow
    );
    this.stateStoreUtilsService.setShipFulfillment({ shipTo: isShipToFlow });
    this.userService.p10CustomerLoggedIn$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((value: boolean) => {
        isP10mCustomer = value;
      });
    // if (this.commonUtilService.isRetailChannel() && !isP10mCustomer) {
    //   this.router.navigateByUrl('plan-services', { state: { data: 'plan-services', isShipToFlow: isShipToFlow, scanToCart: scanToCart } });
    //   if (scanToCart && !isShipToFlow) {
    //     this.openScanToCartModal.next(scanToCart);
    //   }
    //   return true;
    // }
    return false;
  }

  /**
   * Remove tile from active tiles
   * @param tileId
   */
  private removeTileFromActiveTiles(tileId?: string): void {
    tileId = tileId || this.stateStoreUtilsService.getTileIdInFocus();
    const currentActiveTiles = this.stateStoreUtilsService.getActiveTiles();
    const removeTileIndex = currentActiveTiles.indexOf(tileId);
    if (removeTileIndex > -1) {
      currentActiveTiles.splice(removeTileIndex, 1);
      this.stateStoreUtilsService.deleteTileModel(tileId);
      this.stateStoreUtilsService.setActiveTiles(currentActiveTiles);
    }
  }

  /**
   * Compare services in cart with selected line services
   * set delta
   * @returns boolean
   */
  private compareWithCartServices(serviceBundle: EligibleServices, selectedLine: Line): boolean {
    if (serviceBundle && selectedLine) {
      // Get Selected services offer Id Array  for a given Line
      let selectedServices: string[];
      serviceBundle.services.category.forEach((category: Category) => {
        category.family.forEach((currentFamliy: ServiceFamily) => {
          const services: string[] = currentFamliy.details
            .filter((service: CartService) => service.selected === true)
            .map((service: CartService) => service.offerId);
          selectedServices = selectedServices.concat(services);
        });
      });
      // Get Cart Services for a given respective line
      const servicesInCart: string[] = selectedLine.services.map(
        (service: CartItem) => service.offerId
      );
      // Compare Selected services Array with Cart services Array, return boolean
      const isServiceDelta: boolean = selectedServices.every((value: string, index: number) => {
        const serviceIndexCart: number = servicesInCart.indexOf(value);
        return value === servicesInCart[serviceIndexCart] ? false : true;
      });
      return isServiceDelta;
    }
  }

  /**
   * Get Complete Line
   * open Modal to navigate Cart or delete partial lines
   */
  private getCompleteLines(): string[] {
    const completeTiles = [];
    const activeTiles = this.stateStoreUtilsService.getActiveTiles();
    if (activeTiles) {
      activeTiles.forEach((tileId: string) => {
        const tileModel = this.stateStoreUtilsService.getTileModelForTileId(tileId);
        if (tileModel && tileModel.lineId) {
          const line: CartLine = this.getLineFromCart(tileModel.lineId);
          const device = line.device;
          const cartLinePlan = CartUtils.getCartItemAsArrayFromLine(
            line,
            CART_ITEM_STATUS.ITEM_ADD,
            'plan'
          );
          const plan = cartLinePlan.length ? cartLinePlan[0] : null;
          if ((device && device.offerId) || (plan && plan.offerId)) {
            completeTiles.push(tileId);
          }
        }
      });
    }
    return completeTiles;
  }

  /**
   * Make a post request to add a device to cart.
   * @param sku string
   * @param offerId string
   * @param quantity number
   * @param pricingOption string
   */
  public postAccessoryToCart(
    skuCollection?: AddSkuItemsToNonLineInterface[],
    overrideIndicator?: boolean
  ): Observable<Cart> {
    const requestObject = {
      skuDetails: skuCollection,
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      accountId: this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId(),
      transactionTypes: TRANSACTION_TYPE.PURCHASEEQUIPMENT,
    };

    requestObject.accountId =
      requestObject.accountId === CART_REVIEW_CONST.ANON ? '' : requestObject.accountId;

    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };

    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator.toString());
    }
    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2107_addingDeviceToNonLineItem,
        requestObject,
        header,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<Cart>) => {
          // tslint:disable-next-line: no-string-literal
          const cartData = response?.body['cart'];
          this.stateStoreUtilsService.setCartId(cartData.cartId);
          this.stateStoreUtilsService.setCart(cartData);
          this.announceCartUpdate(cartData);
          this.stateStoreUtilsService.setCartEditFlag(true);
          return cartData;
        }),
        catchError((error: APIError[]) => observableThrowError(error))
      );
  }

  /**
   * Function to group cartItems by sku and return the same with sum in quantity field
   */
  public groupCartItemsBySku(accessories: CartItem[]): CartItem[] {
    const accessoryCountMap: Map<string, CartItem> = new Map<string, CartItem>();
    accessories.map((cartItem: CartItem) => {
      let accessory: CartItem = accessoryCountMap.get(cartItem.sku);
      if (accessory) {
        accessory.quantity = accessory.quantity + 1;
        accessory.totalDisplayPrice =
          accessory.totalDisplayPrice + accessory.price.priceSummary.payNowPrice.salePrice.amount;
      } else {
        accessory = cartItem;
        accessory.quantity = accessory.quantity || 1;
        accessory.totalDisplayPrice = accessory.price.priceSummary.payNowPrice.salePrice.amount;
      }
      accessoryCountMap.set(cartItem.sku, accessory);
    });
    return Array.from(accessoryCountMap.values());
  }

  /**
   * Function to get plan to service conflict
   * @param cartResponse of API 2109
   * @param lineId associated line id
   * @return an array of conflicted services with plan of type CartItem
   */
  public getConflictedServices(cartResponse: Cart, lineId: string): CartItem[] {
    if (cartResponse) {
      const currentLine = cartResponse.lines.find((line: CartLine) => line.id === lineId);
      const services = currentLine ? currentLine.services : [];
      return services.filter((service: CartItem) => {
        return service.statusCode.toUpperCase() === CPS_CONSTANTS.REMOVE;
      });
    }
  }

  /**
   * Function used to check if epin has conflict with cart or not
   * @param checkOnlyForEPins Its used in PDP screen to check when we add device it's conflict with both BYOS scenario
   * and E-Pin. If coming from browse plans or BYOD it needs to set to false.
   * @param productTypes
   * @returns EpinConflictResponse
   */
  public isEpinConflictWithCart(
    checkOnlyForEPins: boolean = true,
    productTypes: ProductTypes = ProductTypes.HARDGOOD
  ): EpinConflictResponse {
    const updatedCart: Cart = this.stateStoreUtilsService.getCart();
    if (!updatedCart) {
      return null;
    }
    let hasConflict = this.checkForEpinConflict(updatedCart, productTypes);
    if (!checkOnlyForEPins) {
      hasConflict = hasConflict || this.isByosFlow();
    }
    // ProductTypes.SOFTGOOD required. Since softgood not compatible with E-Pin even for AUTH customer (browse plans)
    if (
      ((!this.stateStoreUtilsService.getCustomerId() || productTypes === ProductTypes.SOFTGOOD) &&
      hasConflict) || (this.stateStoreUtilsService.getCustomerId()  && productTypes === ProductTypes.HARDGOOD && hasConflict)
    ) {
      return this.getEpinConflictStatus();
      // switch (SETTINGS.CHANNEL) {
      //   case CHANNELS.CARE:
      //   case CHANNELS.WEB:
      //     return this.getEpinConflictStatus();
      //   case CHANNELS.RETAIL:
      //     const getShipFulfillment: ShipTo = this.stateStoreUtilsService.getShipFulfillment();
      //     if (this.objectService.get(getShipFulfillment, 'shipTo')) {
      //       return this.getEpinConflictStatus();
      //     }
      //     break;
      //   default:
      // }
    }
  }

  /**
   * Function used to check initially if conflict exist or not if we send ProductTypes as hardgood, then it will check
   * if we have EPIN in cart. If so then it will return true to say it has conflict
   * @param updatedCart
   * @param productTypes
   * @returns boolean
   */
  public checkForEpinConflict(updatedCart: Cart, productTypes: ProductTypes): boolean {
    let hasEpin: boolean = false;
    let hasHardGood: boolean = false;
    if (this.objectService.get(updatedCart, 'accessories')) {
      updatedCart.accessories.forEach((value: CartItem) => {
        if (
          this.objectService.get(value, 'productSubType.0').toUpperCase() ===
          HARDGOODS_CONSTANTS.VOUCHER
        ) {
          hasEpin = true;
        } else {
          hasHardGood = true;
        }
      });
    }
    let deviceInNewLine: boolean = false;
    if (this.objectService.get(updatedCart, 'lines')) {
      updatedCart.lines.forEach((currentLine: CartLine) => {
        if (currentLine.device && currentLine.device.actionCode !== HARDGOODS_CONSTANTS.RETAIN) {
          deviceInNewLine = true;
        }
      });
    }
    hasHardGood =
      hasHardGood || deviceInNewLine || this.objectService.get(updatedCart, 'devices.length') > 0;
    if (productTypes === ProductTypes.EPIN) {
      return hasHardGood;
    } else {
      return hasEpin;
    }
  }

  /**
   * Function used to if there is any due now amount
   * @returns boolean
   */
  public checkIfDueNowAmount(): boolean {
    const cartResponse = this.stateStoreUtilsService.getCart();
    const vouchers = this.objectService.get(cartResponse, 'funds.vouchers') || [];
    const oneTimeAdj = this.objectService.get(cartResponse, 'oneTimeAdj') || '0.0';
    if (
      this.objectService.get(
        cartResponse,
        'pricing.subTotal.priceSummary.payNowPrice.salePrice.amount'
      ) ||
      this.objectService.get(cartResponse, 'funds.vouchers') ||
      this.objectService.get(cartResponse, 'funds.usedBalance')
    ) {
      return (
        cartResponse.pricing.subTotal.priceSummary.payNowPrice.salePrice.amount +
          Number(oneTimeAdj) >
          0 ||
        vouchers.length > 0 ||
        Number(this.objectService.get(cartResponse, 'funds.usedBalance')) >= 0
      );
    }
    return false;
  }

  /**
   * method used for updating tax by exempting the tax applied
   */
  public exemptTaxOnReviewOrder(taxExemptionFormGroup: FormGroup): void {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };
    const requestPayload = {
      organizationName: taxExemptionFormGroup.get('taxExemptionText').value,
    };
    this.apiGatewayService
      .postRequest(API_MAP.API_2501_taxExemption, requestPayload, null, null, pathParams)
      .subscribe((response: HttpResponse<Cart>) => {
        // tslint:disable-next-line: no-string-literal
        const cartData = response?.body['cart'];
        this.stateStoreUtilsService.setCart(cartData);
        this.announceCartUpdate(cartData);
      });
  }

  /**
   * Function to post validates sim and IMEI to device in cart
   * @param {CartReview} validatedItems
   * @returns {Observable<Response>}
   */
  public postValidatedItemsToCart(validatedItems: CartValidatedItem[]): Observable<CartResponse> {
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
    };
    let requestPayload = {};
    if (this.checkForRemorseExchange()) {
      requestPayload = {
        transactionType: ORDER_TYPE.EXCHANGE,
        validateItems: validatedItems,
      };
    } else {
      requestPayload = { validateItems: validatedItems };
    }
    return this.apiGatewayService
      .postRequest(API_MAP.API_2123_postItemValidation, requestPayload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<CartResponse>) => {
          const cartData = response?.body.cart;
          this.stateStoreUtilsService.setCart(cartData);
          this.announceCartUpdate(cartData);
          return response?.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method to check whether tourist plan is present in cart
   * @returns boolean
   */
  public isTouristPlanInCart(): boolean {
    const cart = this.getStatestoreCartReference();
    if (cart) {
      const offerId = this.objectService.get(cart, 'lines.0.plan.0.offerId');
      const offerIdinLowerCase = offerId && offerId.toLowerCase();
      return offerIdinLowerCase && offerIdinLowerCase.indexOf(HARDGOODS_CONSTANTS.TOURIST) > -1;
    }
  }

  /**
   * Method to check whether current lines exceed max voice lines
   * @returns boooean
   */
  public isMaxLineReached(): boolean {
    const updatedCart: Cart = this.stateStoreUtilsService.getCart();
    return this.objectService.get(updatedCart, 'linesRemaining') === 0;
  }

  /**
   * Function used to check if current flow is BYOS
   * @returns {boolean}
   */
  public isByosFlow(): boolean {
    const updatedCart: Cart = this.stateStoreUtilsService.getCart();
    let firstLine: CartLine;
    if (this.objectService.get(updatedCart, 'lines')) {
      firstLine = updatedCart.lines.find((line: CartLine) => {
        return line.lineStatus === 'ADDED' || line.lineStatus === 'CHANGED';
      });
    }
    return (
      this.objectService.get(firstLine, 'simNumber') && !this.objectService.get(firstLine, 'device')
    );
  }

  /**
   * Method to check whether byos is present in cart
   * @returns boolean
   */
  public isByosInCart(): boolean {
    const cart = this.stateStoreUtilsService.getCart();
    const lines = this.objectService.get(cart, 'lines');
    const lineLength = lines && lines.length;
    let byosInCart = false;
    for (let i = 0; i < lineLength; i++) {
      if (
        !this.objectService.get(lines[i], 'device') &&
        this.objectService.get(lines[i], 'simNumber')
      ) {
        byosInCart = true;
        break;
      }
    }
    return byosInCart;
  }

  /**
   * Function used to check if user is in device upgrade flow
   * @returns boolean
   */
  public isDeviceUpgradeFlow(): boolean {
    const cart = this.getStatestoreCartReference();
    const orderType = this.objectService.get(cart, 'orderType');
    if (this.commonUtilService.toUpperCase(orderType) === ORDER_TYPE.PURCHASEEQUIPMENT) {
      const lines: CartLine[] = this.objectService.get(cart, 'lines');
      if (this.commonUtilService.isNotEmpty(lines)) {
        const upgradeLine = lines.find((line: CartLine) => {
          return this.objectService.get(line, 'device.actionCode') === CART_REVIEW_CONST.ADD;
        });
        return !!upgradeLine;
      }
    }
    return false;
  }

  /**
   * Function used to check Epin has conflict in web or care
   * @returns EpinConflictResponse
   */
  private getEpinConflictStatus(): EpinConflictResponse {
    const listOfDevices: string[] = [];
    const tempResponse = this.getDeviceNamesFromCart();
    let tempAllDevices = [];
    tempAllDevices = tempAllDevices.concat(
      tempResponse.listOfDevices.lineDevices,
      tempResponse.listOfDevices.nonLineDevice,
      tempResponse.listOfDevices.accessory,
      tempResponse.listOfDevices.epins
    );

    tempAllDevices.forEach((value: string) => {
      listOfDevices.push(value);
    });

    return {
      conflictingMessage: listOfDevices,
      hasOnlyByos: tempResponse.hasByos,
      hasConflict: true,
    };
  }

  /**
   * Function used to get existing cart items
   * @returns {boolean, listOfDevices : {string[], string[]}}
   */
  public getDeviceNamesFromCart(): {
    hasByos: boolean;
    listOfDevices: {
      nonLineDevice: string[];
      lineDevices: string[];
      accessory: string[];
      epins: string[];
    };
  } {
    const updatedCart: Cart = this.stateStoreUtilsService.getCart();
    if (!updatedCart) {
      return null;
    }
    const nonLineDevice: string[] = this.getNonLineDeviceList(
      this.objectService.get(updatedCart, 'devices')
    );
    const getAccessory = this.getAccessorys(this.objectService.get(updatedCart, 'accessories'));
    const accessory: string[] = getAccessory.accessory;
    const epins: string[] = getAccessory.epin;
    const lineLevelDevice = this.getLineLevelDevices(
      updatedCart.lines,
      nonLineDevice,
      accessory.concat(epins)
    );
    return {
      hasByos: lineLevelDevice.hasOnlyByos,
      listOfDevices: {
        nonLineDevice: nonLineDevice,
        lineDevices: lineLevelDevice.tempLineLevelDevices,
        accessory: accessory,
        epins: epins,
      },
    };
  }

  /**
   * Method to set the current page value based on URL
   * @returns void
   */
  public setCurrentPage(): void {
    let url = this.router.url ? this.router.url : '';
    url = url.split('?')[0];
    switch (url) {
      case CART_ROUTES.PAYMENT:
      case CART_ROUTES.PAYMENT_SHIPTO:
      case CART_ROUTES.PAYMENT_RETAIL:
        this.currentPage.next(CART_REVIEW_CONST.PAYMENT);
        break;
      case CART_ROUTES.SHIPPING:
        this.currentPage.next(CART_REVIEW_CONST.SHIPPING);
        break;
      case CART_ROUTES.CART_REVIEW:
        this.currentPage.next(CART_REVIEW_CONST.CART_REVIEW);
        break;
      case CART_ROUTES.ORDER_REVIEW:
        this.currentPage.next(CART_REVIEW_CONST.ORDER_REVIEW);
        break;
      case CART_ROUTES.ACCOUNT_SETUP:
        this.currentPage.next(CART_REVIEW_CONST.ACCOUNT_SETUP);
        break;
      case CART_ROUTES.LINE_SETUP:
        this.currentPage.next(CART_REVIEW_CONST.LINE_SETUP);
        break;
      default:
        this.currentPage.next(this.router.url);
    }
  }

  /**
   * Function used to get list of non line level device offer id
   * @param {CartItem[]} devices
   * @returns {string[]}
   */
  private getNonLineDeviceList(devices: CartItem[]): string[] {
    const tempListOfNonLineDevices: string[] = [];
    devices.forEach((device: CartItem) => {
      const devicePrice = this.objectService.get(
        device,
        'price.priceSummary.payNowPrice.salePrice.display'
      );
      let offerId = this.objectService.get(device, 'offerName') || '';
      offerId = this.getOfferIdWithPrice(offerId, devicePrice);
      tempListOfNonLineDevices.push(offerId);
    });
    return tempListOfNonLineDevices;
  }

  /**
   * Function used to get line level devices offer if BYOS return plan name
   * @param {CartItem[]} lines
   * @param {string[]} nonLineDevice
   * @returns {{tempLineLevelDevices: string[]; hasByos: boolean}}
   */
  private getLineLevelDevices(
    lines: CartItem[],
    nonLineDevice: string[],
    accessory: string[]
  ): { tempLineLevelDevices: string[]; hasOnlyByos: boolean } {
    const tempLineLevelDevices: string[] = [];
    let hasOnlyByos = false;
    lines.forEach((value: CartLine) => {
      const lineStatus = this.objectService.get(value, 'lineStatus');
      if (lineStatus === 'ADDED' || lineStatus === 'CHANGED') {
        const isDeviceExist = this.objectService.get(value, 'device');
        if (isDeviceExist) {
          const devicePrice = this.objectService.get(
            value,
            'device.price.priceSummary.payNowPrice.salePrice.display'
          );
          let offerId = this.objectService.get(value, 'device.offerId') || '';
          offerId = this.getOfferIdWithPrice(offerId, devicePrice);
          tempLineLevelDevices.push(offerId);
        } else if (!isDeviceExist && nonLineDevice.length === 0 && accessory.length === 0) {
          if (tempLineLevelDevices.length === 0) {
            hasOnlyByos = true;
          }
          tempLineLevelDevices.push(this.objectService.get(value, 'plan.0.offerName'));
        }
      }
    });
    return {
      tempLineLevelDevices,
      hasOnlyByos,
    };
  }

  /**
   * Function used to get list of accessory
   * @param {CartItem[]} accessorys
   * @returns {string[], string[]}
   */
  private getAccessorys(accessorys: CartItem[]): { accessory: string[]; epin: string[] } {
    const tempAccessoryList: string[] = [];
    const tempEpinList: string[] = [];
    accessorys.forEach((value: CartItem) => {
      const price = this.objectService.get(
        value,
        'price.priceSummary.payNowPrice.salePrice.display'
      );
      let offerId = value.offerId || '';
      offerId = this.getOfferIdWithPrice(offerId, price);
      if (value.productSubType[0].toUpperCase() !== HARDGOODS_CONSTANTS.VOUCHER) {
        tempAccessoryList.push(offerId);
      } else {
        tempEpinList.push(offerId);
      }
    });
    return {
      accessory: tempAccessoryList,
      epin: tempEpinList,
    };
  }

  /**
   * Method to get the offerId and price
   * @param offerId
   * @param price
   * @returns string
   */
  private getOfferIdWithPrice(offerId: string, price: string): string {
    const hyphenWithSpace = SYMBOL_CONSTANTS.SPACE.concat(SYMBOL_CONSTANTS.HYPHEN).concat(
      SYMBOL_CONSTANTS.SPACE
    );
    return offerId.concat(hyphenWithSpace).concat(price || '');
  }

  /**
   * Method to get the paymentError
   * @param submitOrderResponse
   * @returns boolean
   */
  private validatePaymentErrors(submitOrderResponse: SubmitOrderResponse): boolean {
    return (
      submitOrderResponse.isDecryptError ||
      submitOrderResponse.isMinMaxError ||
      submitOrderResponse.isNegativeFileError
    );
  }

  /**
   * Used to check whether payment was approved or declined
   * @param {SubmitOrderResponse} submitOrderResponse
   * @param {string} actionCode
   * @returns {boolean}
   */
  // tslint:disable:cyclomatic-complexity
  private isValidPayment(submitOrderResponse: SubmitOrderResponse, actionCode: string): boolean {
    if (actionCode === 'ADD' || actionCode === SHARED_PAYMENT.SIGNATURE_UPDATE) {
      return (
        (!submitOrderResponse.responseCode ||
          submitOrderResponse.responseCode.toLocaleLowerCase() === SHARED_PAYMENT.APPROVAL ||
          submitOrderResponse.responseCode === SHARED_PAYMENT.PART_AUTH ||
          !(
            this.filterSubmitOrderErrorMessage(
              this.objectService.get(submitOrderResponse, 'errorMessage', [])
            ).length > 0
          )) &&
        this.objectService
          .get(submitOrderResponse, 'fraudCheckIndicator', '')
          .toLocaleLowerCase() !== SHARED_PAYMENT.REJECT &&
        this.objectService
          .get(submitOrderResponse, 'fraudCheckStatus.name', '')
          .toLocaleLowerCase() !== SHARED_PAYMENT.REJECT &&
        !this.validatePaymentErrors(submitOrderResponse)
      );
    } else if (actionCode === 'REMOVE') {
      return (
        this.objectService
          .get(submitOrderResponse, 'responseCode', SHARED_PAYMENT.VOID_PROCESSED)
          .toLocaleLowerCase() === SHARED_PAYMENT.VOID_PROCESSED ||
        this.objectService.get(submitOrderResponse, 'responseCode', '').toLocaleLowerCase() ===
          SHARED_PAYMENT.APPROVAL
      );
    }
    const orderActionCode = this.objectService.get(submitOrderResponse, 'orderStatus.@statusCode');
    return (
      (this.commonUtilService.isNotEmpty(
        ORDER_SUBMIT_SUCCESS.find((orderStatus: string) => {
          return orderStatus === orderActionCode;
        })
      ) &&
        this.objectService
          .get(submitOrderResponse, 'fraudCheckStatus.name', '')
          .toLocaleLowerCase() !== SHARED_PAYMENT.REJECT) ||
      this.objectService
        .get(submitOrderResponse, 'fraudCheckStatus.name', '')
        .toLocaleLowerCase() === SHARED_PAYMENT.REVIEW
    );
  }

  /**
   * Method to check for remorse exchange
   * @returns boolean
   */
  public checkForRemorseExchange(): boolean {
    const cart = this.getStatestoreCartReference();
    let isRemorseExchange = false;
    if (
      cart.orderType === ORDER_TYPE.EXCHANGE &&
      cart.orderReason === ORDER_TYPE.ORDER_REASON_REMORSE
    ) {
      isRemorseExchange = true;
    }
    return isRemorseExchange;
  }

  /**
   * Function to verify the security pin entered
   * @param {string} pin
   * @returns Observable<CustomerDetailsResponse>
   */
  public pinVerification(pin: string): Observable<CustomerDetailsResponse> {
    const userEmailData = this.stateStoreUtilsService.getUserEmailData();
    const customerDetailsRequestPayload = {
      ccid: this.objectService.get(userEmailData, 'ccid'),
      securityPIN: pin,
      accountNumber: this.objectService.get(userEmailData, 'accountNumber'),
      bscsCustomerID: this.objectService.get(userEmailData, 'bscsCustomerId'),
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2007_getCustomerDetails, customerDetailsRequestPayload)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response?.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function used to show payment information if applicable
   * @returns boolean
   */
  public showPaymentInformation(): boolean {
    const cartResponse = this.stateStoreUtilsService.getCart();
    if (this.objectService.get(cartResponse, 'payment.length')) {
      return true;
    }
    return false;
  }

  /**
   * Function to check the line edited or not
   * @returns boolean
   */
  public isAllLineEdited(): boolean {
    if (this.stateStoreUtilsService.isMultiLine()) {
      const cartResponse = this.stateStoreUtilsService.getCart();
      if (cartResponse) {
        const lines = this.objectService.get(cartResponse, 'lines');
        const nonEditedLine = lines.find((line: CartLine) => {
          return line.isLineEdited === false && line.lineStatus !== CPS_CONSTANTS.CANCELLED;
        });
        if (nonEditedLine) {
          return this.objectService.get(nonEditedLine, 'isLineEdited');
        }
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  /**
   * Function to check if a hardgood is in cart
   * @param {Cart} cart
   * @returns {boolean} true if hard good is in cart, false other wise
   */
  public isHardGoodSelected(cart: Cart): boolean {
    let isHardGoodSelected: boolean = false;
    this.objectService.get(cart, 'lines', []).map((line: CartLine) => {
      // @TODO need to check both offerId and sku to determine if device is selected
      if (
        this.objectService.get(line, 'device.offerId') ||
        this.objectService.get(line, 'device.sku')
      ) {
        isHardGoodSelected = true;
      }
    });

    if (
      this.objectService.get(cart, `devices.length`, 0) > 0 ||
      this.objectService.get(cart, `accessories.length`, 0) > 0
    ) {
      isHardGoodSelected = true;
    }
    return isHardGoodSelected;
  }

  /**
   * Method to announce that cart is updated in CPS flow
   * @param response Cart api response
   * @returns void
   */
  public announceCompleteCartUpdate(response: HttpResponse<Object>): void {
    if (response?.body) {
      this.stateStoreUtilsService.setCompleteCartResponse(response?.body);
      this.completeCartUpdatedSource.next(true);
    }
  }

  /**
   * Holds boolean and returns true is store and till is in open state.
   * @returns {boolean}
   */
  public isStoreOpen(): boolean {
    const postTillStatus = this.stateStoreUtilsService.getPOSVirtualTillStatus();
    // return !(SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL
    //   && (!this.stateStoreUtilsService.getStoreStatus() || !postTillStatus));
    return true;
  }

  /**
   * used to get selectedCountry Value
   */
  public getSelectedCountry(): string {
    return this.selectedCountry;
  }

  /**
   * use to set selected country
   * @param countryName
   */
  public setSelectedCountry(countryName: string): void {
    this.selectedCountry = countryName;
  }

  /**
   * method to handle get API 2044 call for cancellation Reasons in the Drop down
   * @param cancelType: requests the api gateway
   * @return reasons
   */
  public getPriceOverRideReasons(type: string): Observable<Reason[]> {
    const headers = new HttpHeaders();
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    return this.apiGatewayService
      .getRequest(
        ASSISTED_API_MAP.API_2044_getReasons,
        headers,
        { type: type },
        null,
        null,
        cacheObj
      )
      .pipe(
        map((response: HttpResponse<Object>) => {
          return this.objectService.get(response?.body, 'reasons', []);
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Function used to check ABF permissions and return true if Rep as permission to add a line
   * @returns {boolean}
   */
  public checkForAddALinePermission(route: string, permission?: string): boolean {
    const isHardStopEnabled = this.stateStoreUtilsService.getIsHardStop();
    this.isAuthCustomer = this.checkIfCustomerAuthenticated();
    if (this.isAuthCustomer && isHardStopEnabled) {
      return false;
    } else if (!isHardStopEnabled && !this.isMaxLineReached()) {
      return true;
    }
    if ((this.appConfig.getConfig('enableMultiLine') || false) === true) {
      return true;
    }
    if (permission) {
      return this.uIPermissionService.checkPermissions(route, permission);
    }
    const cart: Cart = this.stateStoreUtilsService.getCart();
    const configuringMultiLine: boolean = this.objectService.get(cart, 'linesOccupied', 0) >= 1;
    if (configuringMultiLine) {
      return this.uIPermissionService.checkPermissions(route, PERMISSION_CONSTANTS.ADD_A_LINE);
    }
    return true;
  }

  /**
   * method to show epin balance message
   */
  public getEpinMessage(aemData: ConfigurableCartAEMData): string {
    const accessories = this.objectService.get(this.getStatestoreCartReference(), 'accessories');
    let epinPrice: number;
    let epinAmount = 0;
    let showEpinBalance;
    accessories.forEach((product: CartItem) => {
      if (
        this.objectService.get(product, 'productSubType.0').toUpperCase() ===
        HARDGOODS_CONSTANTS.VOUCHER
      ) {
        epinPrice = this.objectService.get(
          product,
          'price.priceSummary.payNowPrice.listPrice.amount'
        );
        epinAmount = epinAmount + epinPrice;
      }
    });
    return (showEpinBalance = this.commonUtilService.messageFormatter(
      this.objectService.get(aemData, 'epinBalanceMessage'),
      [epinAmount.toString()]
    ));
  }

  /**
   * sets flag to show webFraudMessage
   */
  public setWebFraudMessage(showMessage: boolean): void {
    this.showWebFraudMessage = showMessage;
  }

  /**
   * gets flag to show webFraudMessage
   */
  public getWebFraudMessage(): boolean {
    return this.showWebFraudMessage;
  }

  /**
   * method to validate error code
   */
  private validateErrorCode(code: string): void {
    if (ORDER_SUBMIT_ERROR_CODE.indexOf(code) >= 0) {
      this.submitOrderError = true;
      this.router.navigate([CONFIRMATION_ROUTE.ORDER_CONFIRMATION]);
    } else if (PAYMENT_ERROR_CODE.indexOf(code) >= 0) {
      this.paymentFailure = true;
      this.router.navigate([CONFIRMATION_ROUTE.ORDER_CONFIRMATION]);
    } else if (code === PUBLISH_ERROR_CODE) {
      this.publishOrderErrorMessage.next(true);
    } else {
      // if (!this.commonUtilService.isRetailChannel()){
      this.router.navigate([PAYMENT_ROUTES.DEFAULT]);
      // }else {
      //   this.navigateToPayment();
      // }
    }
  }

  public validateZipCode(zipCode: string): Observable<Address[]> {
    const defaultForm = new FormGroup({});
    return this.sharedBrowseService.validateAddress(defaultForm, zipCode);
  }
  /**
   * Used to check whether customer is authenticated
   */
  public checkIfCustomerAuthenticated(): boolean {
    const customerId = this.stateStoreUtilsService.getCustomerId();
    return customerId !== '' && customerId !== 'ANON';
  }

  /**
   * Clear customer information from cache
   */
  public clearCustomerData(isAuthenticated: boolean): void {
    if (!isAuthenticated) {
      const msisdn = this.stateStoreUtilsService.getMsisdn();
      const bscscustomerId = this.stateStoreUtilsService.getBscsCustomerID();
      const billingType = this.stateStoreUtilsService.getCustomerBillingType();
      const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
      // this.stateStoreUtilsService.clearCustomerInformation();
      // To persist minimal data required to render pages for a logged in customer
      this.stateStoreUtilsService.setMsisdn(msisdn);
      this.stateStoreUtilsService.setBscsCustomerID(bscscustomerId);
      this.stateStoreUtilsService.setBillingTypeOfCustomerInFocus(billingType);
      this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);
    } else {
      const interactionID = this.stateStoreUtilsService.getInteractionId();
      this.stateStoreUtilsService.clearCustomerInformation();
      this.stateStoreUtilsService.setInteractionId(interactionID);
    }
  }

  /**
   * method to assign cartScheduleStatus
   * @param requestPayload Usku
   * @param cartScheduleStatus string
   * @param isUsku: boolean
   * @returns void
   */
  private cartScheduleStatus(
    requestPayload: Usku,
    cartScheduleStatus: string,
    isUsku: boolean,
    isAllowEdit?: boolean
  ): void {
    if (isUsku) {
      requestPayload.isUsku = isUsku;
      // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL) {
      //   requestPayload.cartScheduleStatus = cartScheduleStatus;
      // }
    }
    const eSimEnabled = this.featureToggleUtilService.isEsimActivationsEnabled();
    if (eSimEnabled) {
      const isDefault: boolean = this.objectService.get(
        this.stateStoreUtilsService.getSelectedSimInfo(),
        'isDefault'
      );
      if (!isDefault || isAllowEdit) {
        const simType: string =
          this.objectService.get(this.stateStoreUtilsService.getSelectedSimInfo(), 'simType') || '';
        requestPayload.activationType = simType.toLocaleUpperCase();
      }
    }
  }
  /**
   * Method to check whether test drive plan is present in cart
   * @returns boolean
   */
  public checkTestDrivePlanInCart(): boolean {
    const cart = this.getStatestoreCartReference();
    const eSimEnabled = this.featureToggleUtilService.isEsimEnabled();
    const isTestDrivePlan = this.objectService.get(cart, HARDGOODS_CONSTANTS.IS_TEST_DRIVE, false);
    // return eSimEnabled && SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE && isTestDrivePlan;
    return false;
  }

  private getCartItemCount(cart: Cart): string {
    let cartItems = [];
    cart.lines.forEach((line: CartLine) => {
      const lineCartItem = this.getCartItemsOfLine(line);
      cartItems.push(...lineCartItem);
    });
    const DeviceOrAccessoryCartItems = this.getDeviceOrAccessory(cart.devices, cart.accessories);
    cartItems.push(...DeviceOrAccessoryCartItems);
    return cartItems.length.toString();
  }

  public getCartItemsOfLine(line: CartLine): CartItem[] {
    let lineCartItems = [];
    let addedPlans: CartItem[] = [];
    let addedServices: CartItem[] = [];
    if (line.device && line.device.actionCode === CART_ITEM_STATUS.ITEM_ADD) {
      lineCartItems.push(line.device);
    }
    if (line.plan) {
      addedPlans = line.plan.filter((plan: CartItem) => {
        return plan.actionCode === CART_ITEM_STATUS.ITEM_ADD;
      });
    }
    if (line.services) {
      addedServices = line.services.filter((service: CartItem) => {
        return service.cartItemStatus === CART_ITEM_STATUS.ITEM_ADDED;
      });
    }
    if (line.simCard && line.simCard.actionCode === CART_ITEM_STATUS.ITEM_ADD) {
      lineCartItems.push(line.simCard);
    }
    lineCartItems = [...lineCartItems, ...addedPlans, ...addedServices];
    return lineCartItems;
  }

  public getDeviceOrAccessory(devices: CartItem[], accessories: CartItem[]): CartItem[] {
    let DeviceOrAccessoryCartItems = [];
    let addedDevices: CartItem[] = [];
    let addedAccessories: CartItem[] = [];
    if (devices) {
      addedDevices = devices.filter((device: CartItem) => {
        return device.actionCode === CART_ITEM_STATUS.ITEM_ADD;
      });
    }
    if (accessories) {
      addedAccessories = accessories.filter((accessorie: CartItem) => {
        return accessorie.actionCode === CART_ITEM_STATUS.ITEM_ADD;
      });
    }
    DeviceOrAccessoryCartItems = [...addedDevices, ...addedAccessories];
    return DeviceOrAccessoryCartItems;
  }

  public updatePDLCartObjectAndBlockNUpdates(
    cart: OrderSummaryInterface,
    blockNTimes: number
  ): void {
    this.updatePDLData(null, cart);
    this.nTimesCartPDLIsBlocked = blockNTimes;
  }

  /**
   * Update PDL Object with latest cart info
   * @param newCart
   */
  private updatePDLData(cart: Cart, order?: OrderSummaryInterface): void {
    if (order) {
      this.objectService.set(window, PDL_DIGITAL_PATH.CART.PATH, {
        ...this.objectService.get(window, PDL_DIGITAL_PATH.CART.PATH),
        cartid: order.orderId,
        lineCount:
          order.orderItems && order.orderItems.lines
            ? order.orderItems.lines.length.toString()
            : this.objectService.get(window, PDL_DIGITAL_PATH.CART.LINE_COUNT),
        price: {
          subtotal: {
            dueToday: order.priceCollection
              .filter((collection: PriceCollection) => {
                return collection.priceType === PDL_ORDER.PRICE_COLLECTION.SUBTOTAL;
              })[0]
              .priceEntity.amount.toString(),
          },
          taxesFees: {
            dueToday: order.priceCollection
              .filter((collection: PriceCollection) => {
                return collection.priceType === PDL_ORDER.PRICE_COLLECTION.TAX_AND_FEE;
              })[0]
              .priceEntity.amount.toString(),
          },
          total: {
            dueToday: order.priceCollection
              .filter((collection: PriceCollection) => {
                return collection.priceType === PDL_ORDER.PRICE_COLLECTION.TOTAL;
              })[0]
              .priceEntity.amount.toString(),
          },
          shipping: {
            dueToday: order.priceCollection
              .filter((collection: PriceCollection) => {
                return collection.priceType === PDL_ORDER.PRICE_COLLECTION.SHIPPING;
              })[0]
              .priceEntity.amount.toString(),
          },
        },
      });
      if (order.paymentMethods.length > 0) {
        let typeCode = '';
        let paymentMethodCode = '';
        order.paymentMethods.forEach((paymentMethod: PaymentMethods) => {
          const pdlPayment = this.stateStoreUtilsService.getPdlCartPaymentConstant(
            paymentMethod.paymentDetails.paymentMethodCode
          );
          if (pdlPayment) {
            let method: string = paymentMethod.paymentDetails.paymentMethodCode;
            let type: string = paymentMethod.paymentDetails.typeCode;
            if (pdlPayment.method) {
              method = pdlPayment.method;
            }
            if (pdlPayment.type) {
              type = pdlPayment.type;
            }
            paymentMethodCode += paymentMethodCode
              ? PDL_SYMBOL.MULTIPLE_VALUE_SEPARATOR + method
              : method;
            typeCode += typeCode ? PDL_SYMBOL.MULTIPLE_VALUE_SEPARATOR + type : type;
          }
        });
        this.objectService.set(window, PDL_DIGITAL_PATH.CART.PAYMENT.TYPE, typeCode);
        this.objectService.set(window, PDL_DIGITAL_PATH.CART.PAYMENT.METHOD, paymentMethodCode);
      }
    } else {
      if (this.nTimesCartPDLIsBlocked <= 0) {
        this.objectService.set(window, PDL_DIGITAL_PATH.CART.PATH, {
          ...this.objectService.get(window, PDL_DIGITAL_PATH.CART.PATH),
          cartid: cart.cartId,
          lineCount: (
            cart.linesOccupied - this.stateStoreUtilsService.getExistingLinesValue()
          ).toString(),
          totalItem: this.getCartItemCount(cart),
          price: {
            subtotal: {
              dueToday: this.objectService
                .get(
                  cart,
                  PDL_CART_PATH.PRICING.SUB_TOTAL.PRICE_SUMMARY.PAY_NOW_PRICE.SALE_PRICE.AMOUNT,
                  0
                )
                .toString(),
              dueMonthly: this.objectService
                .get(
                  cart,
                  PDL_CART_PATH.PRICING.SUB_TOTAL.PRICE_SUMMARY.MONTHLY_PRICE.SALE_PRICE.AMOUNT,
                  0
                )
                .toString(),
            },
            taxesFees: {
              dueToday: this.objectService
                .get(cart, PDL_CART_PATH.PRICING.TAX.TAX_TOTAL.AMOUNT, 0)
                .toString(),
            },
            total: {
              dueToday: this.objectService
                .get(cart, PDL_CART_PATH.PRICING.TOTAL.PAY_NOW_PRICE.SALE_PRICE.AMOUNT, 0)
                .toString(),
              dueMonthly: this.objectService
                .get(cart, PDL_CART_PATH.PRICING.TOTAL.MONTHLY_PRICE.SALE_PRICE.AMOUNT, 0)
                .toString(),
            },
          },
        });
        const shippingModel = this.stateStoreUtilsService.getShippingModel();
        if (
          shippingModel &&
          this.objectService.get(
            shippingModel,
            PDL_SHIPPING_PATH.SELECTED_SHIPPING_OPTION.SHIPMENT_OPTION_ID
          )
        ) {
          this.objectService.set(
            window,
            PDL_DIGITAL_PATH.CART.PRICE.SHIPPING.DUE_TODAY,
            this.objectService
              .get(shippingModel, PDL_SHIPPING_PATH.SELECTED_SHIPPING_OPTION.COST.AMOUNT, 0)
              .toString()
          );
          this.objectService.set(
            window,
            PDL_DIGITAL_PATH.CART.SHIPPING.DUE_TODAY,
            this.objectService
              .get(shippingModel, PDL_SHIPPING_PATH.SELECTED_SHIPPING_OPTION.COST.AMOUNT, 0)
              .toString()
          );
          this.objectService.set(
            window,
            PDL_DIGITAL_PATH.CART.SHIPPING.METHOD,
            this.objectService
              .get(shippingModel, PDL_SHIPPING_PATH.SELECTED_SHIPPING_OPTION.OPTION_DISPLAY_NAME, 0)
              .toString()
          );
        }
      } else {
        this.nTimesCartPDLIsBlocked--;
      }
    }
  }

  public fetchCartCount(): number {
    const cart = this.stateStoreUtilsService.getCart();
    const cartItemCount = this.getCartlevelItemCount();
    return cartItemCount;
  }

  private getCartlevelItemCount(): number {
    const cart = this.stateStoreUtilsService.getCart();
    const deviceLevel = this.objectService.get(cart, 'devices', []);
    const lineLevel = this.objectService.get(cart, 'lines', []);
    const accessoryLevel = this.objectService.get(cart, 'accessories', []);
    let count = 0;
    // calculating line level items.
    lineLevel.forEach((line: CartLine) => {
      if (
        (line.plan && line.plan.length && line.plan[0].actionCode !== CART_REVIEW_CONST.RETAIN) ||
        (line.device && line.device.actionCode !== CART_REVIEW_CONST.RETAIN)
      ) {
        count = count + 1;
      }
    });
    count = count + this.getItemCount(deviceLevel) + this.getItemCount(accessoryLevel);
    return count;
  }

  private getItemCount(items: CartItem[]): number {
    const itemArray = items.filter((item: CartItem) => {
      return item.actionCode !== CART_ITEM_STATUS.ITEM_RETAIN;
    });
    return itemArray.length;
  }
  public setPhoenixCookie({ cartItemCount }: { cartItemCount: number }): void {
    const brandCookieValue = this.getCookie('brand');
    const cartCounter = JSON.stringify({ cartCounter: cartItemCount });
    document.cookie = `${this.stateStoreUtilsService.getCartCountCookieName()}= ${encodeURIComponent(cartCounter)}; path=/`;
    document.cookie = `interactionId= ${this.stateStoreUtilsService.getInteractionId()}; path=/`;
    document.cookie = `cartId= ${this.stateStoreUtilsService.getCartId()}; path=/`;
    let cartbrandCookieValue;
    if (!brandCookieValue) {
      cartbrandCookieValue = SHARED_CONSTANTS.PREPAID_BRAND_NAME;
    } else {
      cartbrandCookieValue = brandCookieValue;
    }
    document.cookie = `cartBrand= ${cartbrandCookieValue}; path=/`;
    
  }
  public clearPhoenixCookie({ utcTime }: { utcTime: string }): void {
    document.cookie = `cartId=; expires= ${utcTime}; path=/`;
    document.cookie = `interactionId=; expires= ${utcTime}; path=/`;
    document.cookie = `${this.stateStoreUtilsService.getCartCountCookieName()}=; expires= ${utcTime}; path=/`;
    document.cookie = `cartBrand=; expires= ${utcTime}; path=/`;
  }
  public getCookie(name: string): string {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const popSplit = parts.pop().split(';');
      const afterShift = popSplit.shift();
      return afterShift;
    }
  }
  /**
   * getUTCTime function is to get cookie expiry time
   * @param days
   * @param persistCookie
   * @returns UTC time
   */
  public getUTCTime(days: number, persistCookie: boolean = true): string {
    const now = new Date();
    const time = now.getTime();
    const hours = 24;
    const minutes = 60;
    const seconds = 60;
    const milliseconds = 1000;
    let expireTime;
    if (persistCookie) {
      expireTime = time - days * (hours * minutes * seconds * milliseconds);
    } else {
      expireTime = time - days * (hours * minutes * seconds * milliseconds);
    }
    now.setTime(expireTime);
    return now.toUTCString();
  }
  public isAnon(): boolean {
    const accountId = this.stateStoreUtilsService.getAddFan()
      ? ''
      : this.stateStoreUtilsService.getAccountId();
    return !accountId || accountId === CART_REVIEW_CONST.ANON;
  }

  /**
    * Submits form data to API_2176 and returns cart data
    * @returns Observable<AutopayIntentResponse>
    */
  public postAutopayIntent(payload): Observable<Cart> {
    return this.apiGatewayService
    .postRequest(API_MAP.API_2176_postAutopayIntent, payload, null, null)
    .pipe(
      map((response: HttpResponse<Cart>) => response?.body['cart']),
      map((cart: Cart) => {
        this.announceCartUpdate(cart);
        return cart;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  public checkAutoPayDiscountInPlan(plans: CartItem[]): boolean {
    const plansPromosConfig = this.appConfig.getConfig('plansPromos');
    return plans.some(plan =>
      plan?.price?.promotions?.some(promotion => {
        return this.objectService.get(plansPromosConfig, `${promotion.promoId}.isAutoPayDiscount`);
      })
    );
  }
}
